import { Formatters } from '../shared/lib/formatters';
import { TeamPage } from './TeamPage';
import { icon } from '../shared/components/icons';
import { openLightbox, stripUrlsFor } from '../shared/components/Lightbox';
import { isVideoUrl } from '../shared/lib/mediaKind';
// R110 #3: the feed's search/filter/grouping is pure ESM in lib/ (same pattern
// as woSearch) so `node lib/eodFeed.test.mjs` tests the code the page runs.
import { filterReports, groupByDate, departmentsPresent, dayHeader, collapsedSummary, resultText, filedToday, dayOpen, reportOpen, highlight, hasActiveFilters, addDays, fmtDay, WINDOWS, DEFAULT_WINDOW, RETENTION_DAYS, aiPollSig, aiPollRedraw, aiNoteBar, aiNoteConfirm } from '../../../lib/eodFeed.mjs';
import { kebabHtml, bindKebabs, closeKebab, type KebabItem } from '../shared/components/Kebab';

// ── Daily Ops · EOD reports + daily checklists (R25 H) ──────────────────────
// CHECKLISTS — recurring per-department/person templates ("Ops Manager —
//   Morning: review overnight cleans") that get a fresh run every business day.
// EOD — every agent files a short end-of-day report; the feed is readable by
//   the whole team so the next shift starts informed.
// Backed by /api/assetops: /daily-checklists*, /eod.
//
// R113 #6 (Jesse: "Some buttons on the EOD report tab are cluttered"): same
// data and handlers, less chrome —
//   • the three tabs are the app's .seg-tabs;
//   • the composer is collapsed by default (one summary box + 📷 + Submit);
//     department / blockers / tomorrow appear on focus or once there's text,
//     and once you've filed today it folds to "✓ You filed today at h:mm";
//   • ONE toolbar row: search · department select · Mine · 7d|14d|30d|90d ·
//     muted count · Clear (only when a filter is on). The "All" chip is the
//     select's first option; the window tabs hide (not grey out) while
//     searching; the count no longer repeats the window;
//   • an expanded report has ONE ⋯ menu (Edit report / Add photos) instead of
//     inline ✎ + 📷 inside a click-to-collapse head; photos open in the shared
//     Lightbox; Save is the blue .btn-primary;
//   • every colour is a :root token (no bare hex, no .insp-link leak).
// Every id / data-eod-* hook, eodOpen / eodDayToggles, redrawFeed's partial
// redraw, the R48 edit/photo gate fields and the 6-files-per-request batching
// are unchanged. The ⋯ menu is a page-local <details> (MaintenancePage's
// .mn-dd pattern) — there is no shared Kebab component in this tree.
//
// R115 C5 (Jesse: "AI EOD report … can you set everything up for that?"):
// the nightly AI end-of-day report (GET /eod/ai, built server-side from every
// dash store + the synced Hostaway tables, PII / money-free) renders as the
// FIRST card of its day group — collapsed one-liner, expanded headline /
// sections / Tomorrow / Risks, Sources ⓘ, cost (admin / director only), and
// the shared ⋯ Kebab (Post to Slack again · Copy text · Show facts). One row
// above the composer: Generate now (admin / director) + ＋ Note for tonight
// (office roles). Field roles read the cards only.

const API = '/api/assetops';

interface ChecklistItem { id: string; label: string; }
interface ChecklistTemplate { id: string; name: string; department: string; assignee: string; cadence?: string; items: ChecklistItem[]; }
interface CheckState { done?: boolean; na?: boolean; note?: string; photos?: string[]; by: string; at: string; }
interface ChecklistRun { id: string; template_id: string; date: string; checks: Record<string, CheckState>; }
interface EodEdit { at: string; by: string; fields: string[]; }
interface EodReport { id: string; date: string; person: string; person_email?: string; department: string; summary: string; blockers: string; tomorrow: string; photos?: string[]; edits?: EodEdit[]; at: string; }
// R115 C5: the nightly AI end-of-day report (GET /eod/ai — PII / money-free by
// construction; cost_usd + cost_30d ride only for admin / director).
interface AiNarrative { headline: string; sections: { title: string; bullets: string[] }[]; tomorrow: string[]; risks: string[]; slack_text?: string; }
interface AiSlack { posted: boolean; via: string | null; channel: string | null; error: string | null; skipped: string | null; at?: string; }
interface AiReport {
  id: string; date: string; generated_at: string; trigger: 'timer' | 'manual'; by: string | null;
  narrative: AiNarrative | null; narrative_source: 'claude' | 'facts';
  ai: { status: string; kind?: string | null; label?: string | null; model?: string | null; ms?: number | null; cost_usd?: number | null };
  slack: AiSlack | null; sources: Record<string, any>;
}
interface AiStatus {
  armed: boolean; via: string | null; reason: string | null; hour: number; hour_label: string; tz: string; model: string;
  ai_configured: boolean; last_timer_run: string | null; running: { started_at: string; trigger: string; date: string } | null;
  last_error: { at: string; error: string } | null; slack: { bot: boolean; webhook: boolean; on: boolean; channel: boolean }; inbox_ready: boolean;
  cost_30d?: { usd: number; runs: number; days: number };
}
interface AiNote { id: string; date: string; by: string; by_email: string; source: string; text: string; subject?: string; from_domain?: string; at: string; late?: boolean; }
const AI_OFFICE = ['admin', 'director', 'manager', 'remote_ops'];
const AI_MONEY = ['admin', 'director'];
// short pill words for a failed / skipped narrative (the full label is the tooltip)
const AI_KIND_SHORT: Record<string, string> = {
  credits: 'out of credits', auth: 'key invalid', rate_limit: 'rate-limited', overloaded: 'overloaded', parse: 'unreadable reply',
  empty: 'empty reply', no_key: 'not set up', network: 'network error', timeout: 'timed out', refusal: 'declined', truncated: 'cut off',
  interrupted: 'interrupted',   // R115 fix (eod 4): the run died mid-call (redeploy) — served by GET /eod/ai, read-side
};

const DEPARTMENTS = ['Operations', 'Housekeeping', 'Maintenance', 'Guest Experience', 'Onboarding', 'Revenue', 'Admin'];
const CADENCES: [string, string][] = [['daily', 'Daily'], ['weekly', 'Weekly (resets Monday)'], ['monthly', 'Monthly (resets the 1st)']];
const CADENCE_BADGE: Record<string, string> = { daily: '', weekly: '📅 Weekly · resets Mon', monthly: '📅 Monthly · resets the 1st' };
const isVideo = (u: string) => isVideoUrl(u);   // R116 #5: the one shared rule (shared/lib/mediaKind)

export class EodPage {
  private container: HTMLElement;
  private tab: 'checklists' | 'eod' | 'schedule' = 'checklists';
  private date = '';                 // checklist day being viewed ('' until first load)
  private today = '';
  private retainedFrom = '';         // oldest date the server keeps runs for (read-only past it)
  private loadFailed = false;        // R34: last checklist load errored — show retry, never "no checklists"
  private tplSaving = false;
  private templates: ChecklistTemplate[] = [];
  private runs: ChecklistRun[] = [];
  private reports: EodReport[] = [];
  private loading = false;
  private manage = false;            // template-editor mode
  private editTpl: ChecklistTemplate | null = null;
  private eodDraft = { summary: '', blockers: '', tomorrow: '', department: '' };
  private eodPending = false;
  // R113 #6: "Add another" after you've already filed today re-opens the form
  // (cleared again once the next report is in).
  private composerAdd = false;
  // R29: photos picked for the NEXT submit + which report is in inline edit
  private eodFiles: File[] = [];
  private editingReport: string | null = null;
  private editDraft: { id: string; summary: string; blockers: string; tomorrow: string } | null = null;   // what's typed in the inline editor — survives every feed redraw
  // R110 #3: feed filters + what's expanded. Open ids live in a Set so a redraw
  // (an inline edit, a photo upload) never collapses the card it happened in;
  // the user's own day-group toggles are remembered the same way.
  private eodQ = '';
  private eodDept = 'all';
  private eodMine = false;
  private eodDays: number = DEFAULT_WINDOW;
  private eodOpen = new Set<string>();
  private eodDayToggles = new Map<string, boolean>();
  // R115 C5: the AI end-of-day report — one card per day group (latest per
  // date), the Generate-now + note row above the composer (office roles), and
  // what each card has open (expanded / Sources ⓘ / facts) across redraws.
  private aiReports: AiReport[] = [];
  private aiStatus: AiStatus | null = null;
  private aiOpen = new Set<string>();
  private aiSrcOpen = new Set<string>();
  private aiFactsOpen = new Set<string>();
  private aiFacts = new Map<string, any>();
  private aiFlash: { id: string; text: string } | null = null;
  private aiGenerating = false;
  private aiGenMsg = '';
  private aiNotes: AiNote[] = [];        // what the NEXT report reads (GET /eod/ai/notes → notes)
  private aiNotesUsed: AiNote[] = [];    // after tonight's run: the ones it already used (→ included)
  private aiNotesRan: boolean | null = null;   // the queue's ran_tonight (null = an older server)
  private aiNoteOpen = false;
  private aiNoteDraft = '';
  private aiNoteSaving = false;

  constructor(container: HTMLElement) { this.container = container; }

  // R51 Quo: 24h missed calls + inbound texts on Daily Ops. null until the
  // fetch answers; renders nothing when the integration isn't configured.
  private quoRecent: any = null;
  private async loadQuoRecent(): Promise<void> {
    this.quoRecent = await fetch('/api/quo/recent?hours=24').then(r => r.ok ? r.json() : null).catch(() => null);
  }
  private quoCardHtml(): string {
    const q = this.quoRecent;
    if (!q || q.configured === false) return '';
    const missed = q.missed_calls || [];
    const inbound = q.inbound_messages || [];
    // R51: the workspace feed is webhook-driven — say so when it isn't wired,
    // rather than implying a quiet 24h (verified: the Quo API can't poll a line
    // without a participant filter, so there's no fallback).
    if (!missed.length && !inbound.length) {
      const body = q.webhook_pending
        ? 'Connect the Quo webhook (point it at /api/quo/webhook) to see missed calls &amp; new texts here.'
        : 'No missed calls or new texts. ✅';
      return `<div class="eod-card"><div class="eod-card-head"><strong>📞 Quo — last 24h</strong></div><div class="eod-empty" style="padding:10px 14px;">${body}</div></div>`;
    }
    const t = (s: any) => { const d = new Date(String(s || '')); return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };
    return `
      <div class="eod-card">
        <div class="eod-card-head"><strong>📞 Quo — missed calls &amp; new texts (24h)</strong></div>
        <div style="padding:8px 14px; font-size:12.5px; line-height:1.6;">
          ${missed.slice(0, 10).map((c: any) => `<div>☎️ <b style="color:var(--danger-text);">Missed</b> ${Formatters.escapeHtml((c.participants || []).join(', '))} · ${t(c.at)} <span style="color:var(--text-faint);">(${Formatters.escapeHtml(c.line || '')})</span></div>`).join('')}
          ${inbound.slice(0, 10).map((m: any) => `<div>💬 ${Formatters.escapeHtml(String(m.from || ''))}: ${Formatters.escapeHtml(String(m.body || '').slice(0, 90))} <span style="color:var(--text-faint);">· ${t(m.at)}</span></div>`).join('')}
        </div>
      </div>`;
  }

  async render(): Promise<void> {
    this.injectStyles();
    this.loading = true;
    this.draw();
    await Promise.all([this.loadChecklists(), this.loadEod(), this.loadAi(), this.loadAiNotes()]);
    this.loading = false;
    this.draw();
    // Review fix: the Quo /recent fetch can sequentially poll the provider API
    // with no client timeout — it must never block the Daily Ops page load.
    this.loadQuoRecent().then(() => { if (!this.loading) this.draw(); });
  }

  // R113 #6: App.autoRefresh (every 15 min) asks before a full render() — a
  // half-written EOD report, picked-but-unsent photos or a dirty inline edit
  // must survive the timer (same veto HousekeepingPage / TasksPage use).
  suspendAutoRefresh(): boolean {
    if (this.draftHasText() || this.eodPending) return true;
    if (this.aiNoteDraft.trim() || this.aiNoteSaving || this.aiGenerating) return true;   // R115: a half-typed note / a Generate in flight
    return !!(this.editingReport && this.editDirty());
  }
  private draftHasText(): boolean {
    const d = this.eodDraft;
    return !!(d.summary.trim() || d.blockers.trim() || d.tomorrow.trim() || d.department || this.eodFiles.length);
  }

  private actorName(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || sessionStorage.getItem('userRole') || '').trim(); }
    catch (_) { return String(sessionStorage.getItem('userRole') || ''); }
  }
  // R51: checklist items can carry URLs (the GS list links a Slack channel) —
  // escape FIRST, then linkify, so only the URL becomes markup.
  private linkify(s: string): string {
    const esc = Formatters.escapeHtml(String(s == null ? '' : s));
    return esc.replace(/(https?:\/\/[^\s<)]+)/g, u => `<a href="${u}" target="_blank" rel="noopener" style="color:var(--accent);">${u.length > 42 ? u.slice(0, 40) + '…' : u}</a>`);
  }
  // R48: email = the durable ownership key for the per-author edit gate — the
  // display name drifts when a profile gains a full_name after reports exist.
  private actorEmail(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || '').trim().toLowerCase(); }
    catch (_) { return ''; }
  }
  private actorRole(): string {
    try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; }
  }
  private fmtTime(iso: string): string {
    const d = new Date(String(iso || ''));
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  private async loadChecklists(): Promise<void> {
    // R34 #1: a failed load must NEVER render as "no checklists yet" — that
    // reads as deletion. Track the error and show a retry state instead.
    try {
      const r = await fetch(`${API}/daily-checklists${this.date ? `?date=${this.date}` : ''}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      this.templates = j.templates || [];
      this.runs = j.runs || [];
      this.date = j.date;
      this.today = j.today;
      this.retainedFrom = j.retained_from || '';
      this.loadFailed = false;
    } catch { this.loadFailed = true; }
  }

  private async loadEod(): Promise<void> {
    try {
      // R110 #3: fetch the whole retention window once (it's small text) and
      // filter client-side — the window chips and the search never round-trip.
      const r = await fetch(`${API}/eod?days=${RETENTION_DAYS}`);
      const j = await r.json();
      this.reports = j.reports || [];
      if (!this.today) this.today = j.today || '';
    } catch { /* noop */ }
  }

  // R115 C5: the AI reports (latest per date) + the job's status. A failed or
  // forbidden read just leaves the feed without AI cards — never blocks it.
  private async loadAi(): Promise<void> {
    try {
      const r = await fetch(`${API}/eod/ai?days=${RETENTION_DAYS}`);
      if (!r.ok) return;
      const j = await r.json();
      this.aiReports = Array.isArray(j.reports) ? j.reports : [];
      this.aiStatus = j.status || null;
      if (!this.today) this.today = j.today || '';
    } catch { /* the feed renders without AI cards */ }
  }
  private async loadAiNotes(): Promise<void> {
    if (!this.aiOffice()) return;
    try {
      const r = await fetch(`${API}/eod/ai/notes`);
      if (!r.ok) return;
      const j = await r.json();
      this.aiNotes = Array.isArray(j.notes) ? j.notes : [];
      this.aiNotesUsed = Array.isArray(j.included) ? j.included : [];
      this.aiNotesRan = typeof j.ran_tonight === 'boolean' ? j.ran_tonight : null;
    } catch { /* noop */ }
  }
  private aiOffice(): boolean { return AI_OFFICE.includes(this.actorRole()); }
  private aiMoney(): boolean { return AI_MONEY.includes(this.actorRole()); }

  private draw(): void {
    // dash-head is the app's page-header convention (page-title/page-header
    // don't exist in main.css — using them left the h1 browser-default huge).
    const tab = (id: typeof this.tab, label: string) =>
      `<button type="button" class="seg-tab ${this.tab === id ? 'active' : ''}" data-eod-tab="${id}" role="tab" aria-selected="${this.tab === id}">${label}</button>`;
    this.container.innerHTML = `
      <div class="page-content eod-page">
        <div class="dash-head">
          <h1>Daily Ops</h1>
          <p>Recurring daily checklists per department, and everyone's end-of-day reports.</p>
        </div>
        <div class="seg-tabs eod-seg" role="tablist">
          ${tab('checklists', 'Daily Checklists')}
          ${tab('eod', 'EOD Reports')}
          ${tab('schedule', 'Staff Schedule')}
        </div>
        <div id="eod-body">${this.loading ? '<div class="eod-empty">Loading…</div>' : (this.tab === 'checklists' ? this.checklistsHtml() : this.tab === 'eod' ? this.eodHtml() : '')}</div>
      </div>`;
    this.bind();
  }

  // ── checklists tab ─────────────────────────────────────────────────────────
  private checklistsHtml(): string {
    // R34: a failed load with nothing cached must show the retry card even in
    // Manage — an empty manage list reads as "everything got deleted".
    if (this.manage && !(this.loadFailed && !this.templates.length)) return this.manageHtml();
    const quoCard = this.quoCardHtml();   // R51: attention card above the day's checklists
    const isToday = this.date === this.today;
    const cards = this.templates.map(t => {
      // R30 cadence: the server returns each template's ANCHORED run for the
      // viewed date (weekly runs carry Monday's date) — match template only.
      const run = this.runs.find(r => r.template_id === t.id);
      const checks = run ? run.checks : {} as Record<string, CheckState>;
      const settled = t.items.filter(it => checks[it.id]?.done || checks[it.id]?.na).length;
      const pct = t.items.length ? Math.round(settled / t.items.length * 100) : 0;
      const cadBadge = CADENCE_BADGE[t.cadence || 'daily'];
      return `
        <div class="eod-card">
          <div class="eod-card-head">
            <div>
              <strong>${Formatters.escapeHtml(t.name)}</strong>
              <div class="eod-meta">${Formatters.escapeHtml(t.department || '—')}${t.assignee ? ` · ${Formatters.escapeHtml(t.assignee)}` : ''}${cadBadge ? ` · ${cadBadge}` : ''}</div>
            </div>
            <span class="eod-progress ${settled === t.items.length && t.items.length ? 'done' : ''}">${settled}/${t.items.length}</span>
          </div>
          <div class="eod-bar"><div class="eod-bar-fill" style="width:${pct}%;"></div></div>
          ${t.items.map(it => {
            const c = checks[it.id];
            const photoThumbs = (c?.photos || []).map(u => `<a href="${Formatters.escapeHtml(u)}" target="_blank" rel="noopener"><img class="eod-item-ph" src="${Formatters.escapeHtml(u)}" alt="" loading="lazy"></a>`).join('');
            return `
              <div class="eod-item ${c?.done ? 'checked' : ''} ${c?.na ? 'na' : ''}">
                <input type="checkbox" data-check="${t.id}|${it.id}" ${c?.done ? 'checked' : ''} ${c?.na ? 'disabled' : ''}>
                <div class="eod-item-main">
                  <span class="eod-item-label">${this.linkify(it.label)}</span>
                  ${c?.done ? `<span class="eod-item-by">✔ ${Formatters.escapeHtml(c.by || '')} ${c.at ? this.fmtTime(c.at) : ''}</span>` : ''}
                  ${c?.na ? `<span class="eod-item-by" style="color:var(--text-faint);">N/A ${Formatters.escapeHtml(c.by || '')}</span>` : ''}
                  ${c?.note ? `<div class="eod-item-note">📝 ${Formatters.escapeHtml(c.note)}${(c as any).note_by ? ` <span style="color:var(--text-faint);">— ${Formatters.escapeHtml((c as any).note_by)}</span>` : ''}</div>` : ''}
                  ${photoThumbs ? `<div class="eod-item-phs">${photoThumbs}</div>` : ''}
                </div>
                <div class="eod-item-acts">
                  <button class="eod-na ${c?.na ? 'on' : ''}" data-na="${t.id}|${it.id}|${c?.na ? '0' : '1'}" title="Not applicable today">N/A</button>
                  <button class="eod-na" data-note="${t.id}|${it.id}" title="Add a verification note">📝</button>
                  <label class="eod-na" title="Attach a screenshot/photo for verification" style="cursor:pointer;">📷<input type="file" accept="image/*" multiple data-item-photo="${t.id}|${it.id}" style="display:none;"></label>
                </div>
              </div>`;
          }).join('')}
        </div>`;
    }).join('');
    return `
      <div class="eod-toolbar">
        <input type="date" id="eod-date" class="input" value="${this.date}" max="${this.today}" ${this.retainedFrom ? `min="${this.retainedFrom}"` : ''}>
        ${isToday ? '<span class="eod-meta">Today</span>' : `<button type="button" class="btn btn-secondary btn-sm" id="eod-today-btn">Jump to today</button>`}
        <button type="button" class="btn btn-secondary btn-sm" id="eod-manage-btn" style="margin-left:auto;">⚙️ Manage checklists</button>
      </div>
      ${quoCard}
      ${this.loadFailed && this.templates.length
        ? '<div class="eod-empty" style="padding:8px 12px; margin-bottom:10px;">⚠ Latest refresh failed — showing the last loaded data. <button class="btn btn-secondary btn-sm" data-eod-retry style="margin-left:8px;">Retry</button></div>'
        : ''}
      ${this.loadFailed && !this.templates.length
        ? '<div class="eod-empty">⚠ Couldn\'t load the checklists (nothing is deleted — this is a connection hiccup). <button class="btn btn-secondary btn-sm" data-eod-retry style="margin-left:8px;">Retry</button></div>'
        : (cards || '<div class="eod-empty">No checklists yet — hit ⚙️ Manage to create the first one (e.g. "Ops Manager — Morning").</div>')}`;
  }

  private manageHtml(): string {
    const t = this.editTpl;
    return `
      <div class="eod-toolbar">
        <button type="button" class="btn btn-secondary btn-sm" id="eod-manage-back">← Back to checklists</button>
      </div>
      <div class="eod-card">
        <div class="eod-card-head"><strong>${t && t.id ? 'Edit checklist' : 'New checklist'}</strong></div>
        <div class="eod-form">
          <input type="text" id="tpl-name" placeholder="Checklist name — e.g. Ops Manager — Morning" value="${Formatters.escapeHtml(t?.name || '')}">
          <div class="eod-form-row">
            <select id="tpl-dept">
              <option value="">— Department —</option>
              ${DEPARTMENTS.map(d => `<option value="${d}" ${t?.department === d ? 'selected' : ''}>${d}</option>`).join('')}
            </select>
            <select id="tpl-cadence">
              ${CADENCES.map(([v, l]) => `<option value="${v}" ${(t?.cadence || 'daily') === v ? 'selected' : ''}>${l}</option>`).join('')}
            </select>
            <input type="text" id="tpl-assignee" placeholder="Assigned person (optional)" value="${Formatters.escapeHtml(t?.assignee || '')}">
          </div>
          <textarea id="tpl-items" rows="6" placeholder="One check per line…">${Formatters.escapeHtml((t?.items || []).map(i => i.label).join('\n'))}</textarea>
          <div class="eod-form-row">
            <button type="button" class="btn btn-primary" id="tpl-save">${t && t.id ? 'Save changes' : 'Create checklist'}</button>
            ${t && t.id ? `<button type="button" class="btn btn-secondary" id="tpl-cancel">Cancel edit</button>` : ''}
          </div>
        </div>
      </div>
      ${this.templates.map(x => `
        <div class="eod-card eod-card-row">
          <div>
            <strong>${Formatters.escapeHtml(x.name)}</strong>
            <div class="eod-meta">${Formatters.escapeHtml(x.department || '—')}${x.assignee ? ` · ${Formatters.escapeHtml(x.assignee)}` : ''} · ${Formatters.escapeHtml((x.cadence || 'daily'))} · ${x.items.length} checks</div>
          </div>
          <div style="display:flex;gap:6px;">
            <button type="button" class="btn btn-secondary btn-sm" data-tpl-edit="${x.id}">Edit</button>
            <button type="button" class="btn btn-secondary btn-sm eod-danger" data-tpl-del="${x.id}">Delete</button>
          </div>
        </div>`).join('')}`;
  }

  // ── EOD tab ────────────────────────────────────────────────────────────────
  private eodHtml(): string {
    const parts = this.feedParts();
    // R113 #6: #eod-q stays OUTSIDE #eod-feed-ctl (redrawFeed never replaces
    // the input or its caret); the two sit in one flex row that wraps at 390px.
    return `
      <div id="eod-ai-bar">${this.aiBarHtml()}</div>
      ${this.composerHtml()}
      <div class="eod-feedbar">
        <label class="eod-search">${icon('search', 15)}<input type="search" id="eod-q" class="input" value="${Formatters.escapeHtml(this.eodQ)}" autocomplete="off" spellcheck="false" aria-label="Search EOD reports — a person, a department, or words from a summary. Esc clears." placeholder="Search reports"></label>
        <div id="eod-feed-ctl">${parts.ctl}</div>
      </div>
      <div id="eod-feed">${parts.list}</div>`;
  }

  // R113 #6: collapsed by default — one summary box + 📷 + Submit. The
  // department select, Blockers and Tomorrow (.eod-more) show once the summary
  // is focused or any field has text (bind() toggles .open without a redraw,
  // so focus is never lost). Already filed today → one line + "Add another".
  private composerHtml(): string {
    const esc = (s: string) => Formatters.escapeHtml(s);
    const filed = filedToday(this.reports, this.today, this.eodMe()) as EodReport | null;
    if (filed && !this.composerAdd && !this.draftHasText() && !this.eodPending) {
      return `
      <div class="eod-card eod-composer" data-eod-composer>
        <div class="eod-filed"><span>✓ You filed today at ${esc(this.fmtTime(filed.at))}</span><span class="eod-meta">·</span><button type="button" class="btn btn-ghost btn-sm" data-eod-add>Add another</button></div>
      </div>`;
    }
    const open = this.draftHasText() || this.eodPending;
    const n = this.eodFiles.length;
    return `
      <div class="eod-card eod-composer ${open ? 'open' : ''}" data-eod-composer>
        <div class="eod-composer-head"><strong>File your EOD report</strong><span class="eod-meta">${esc(this.actorName() || 'you')} · ${esc(this.today)}</span></div>
        <div class="eod-form">
          <textarea id="eod-summary" rows="${open ? 4 : 2}" placeholder="What happened today — the stuff the rest of the team should know…">${esc(this.eodDraft.summary)}</textarea>
          <div class="eod-more">
            <select id="eod-dept" class="input" aria-label="Department">
              <option value="">— Department (optional) —</option>
              ${DEPARTMENTS.map(d => `<option value="${d}" ${this.eodDraft.department === d ? 'selected' : ''}>${d}</option>`).join('')}
            </select>
            <textarea id="eod-blockers" rows="2" placeholder="Blockers / needs attention (optional)…">${esc(this.eodDraft.blockers)}</textarea>
            <textarea id="eod-tomorrow" rows="2" placeholder="Plan for tomorrow (optional)…">${esc(this.eodDraft.tomorrow)}</textarea>
          </div>
          <div class="eod-form-acts">
            <label class="btn btn-ghost btn-sm" title="Attach photos or a video to this report">📷 Photos${n ? ` (${n})` : ''}<input type="file" id="eod-photos" accept="image/*,video/*" multiple style="display:none;"></label>
            <button type="button" class="btn btn-primary" id="eod-submit" ${this.eodPending ? 'disabled' : ''}>${this.eodPending ? 'Submitting…' : 'Submit report'}</button>
          </div>
        </div>
      </div>`;
  }

  private eodMe(): { name: string; email: string } { return { name: this.actorName(), email: this.actorEmail() }; }
  /** True when the inline editor holds text that differs from the stored report. */
  private editDirty(): boolean {
    const d = this.editDraft;
    if (!d) return false;
    const r = this.reports.find(x => x.id === d.id);
    if (!r) return true;
    return d.summary !== (r.summary || '') || d.blockers !== (r.blockers || '') || d.tomorrow !== (r.tomorrow || '');
  }

  private eodSearching(): boolean { return this.eodQ.trim().length > 0; }

  // R110 #3: the toolbar controls + result count and the day groups, built
  // from ONE filter pass. They render into #eod-feed-ctl / #eod-feed so typing
  // in the search box only redraws the feed — the input (and its caret) is
  // never replaced, and the composer above is left alone.
  private feedParts(): { ctl: string; list: string } {
    const esc = (s: string) => Formatters.escapeHtml(s);
    const state = { q: this.eodQ, dept: this.eodDept, mine: this.eodMine, days: this.eodDays };
    const res = filterReports(this.reports, state, { today: this.today, me: this.eodMe(), keep: this.editingReport });
    const depts = departmentsPresent(this.reports, DEPARTMENTS);
    const active = hasActiveFilters(state);
    // R113 #6: one row — department <select> (its first option replaces the
    // "All" chip), Mine chip, compact 7d|14d|30d|90d seg (hidden, not
    // disabled, while a search covers all 90 days), muted count, Clear only
    // when something is filtered.
    const ctl = `
      <select class="input eod-sel" data-eod-dept="${esc(this.eodDept)}" aria-label="Department">
        <option value="all" ${this.eodDept === 'all' ? 'selected' : ''}>All departments</option>
        ${depts.map(d => `<option value="${esc(d)}" ${this.eodDept === d ? 'selected' : ''}>${esc(d)}</option>`).join('')}
      </select>
      <button type="button" class="chip ${this.eodMine ? 'active' : ''}" data-eod-mine aria-pressed="${this.eodMine}" title="Only the reports you filed">Mine</button>
      <div class="seg-tabs eod-win" role="group" aria-label="Window" ${res.searching ? 'hidden' : ''}>
        ${WINDOWS.map(d => `<button type="button" class="seg-tab ${this.eodDays === d ? 'active' : ''}" data-eod-days="${d}" title="Reports from the last ${d} days">${d}d</button>`).join('')}
      </div>
      <span class="eod-count" aria-live="polite">${esc(resultText(res.rows.length, res.searching))}</span>
      ${active ? `<button type="button" class="btn btn-ghost btn-sm" data-eod-clear title="Back to the last ${DEFAULT_WINDOW} days, everyone, all departments">Clear</button>` : ''}`;

    // R115 C5: the day's AI report rides FIRST in its day group — a day with
    // only an AI report still gets a group; today gets a "runs tonight" line
    // until the report exists. Hidden under a department / Mine filter (it is
    // nobody's department report); a search shows the ones whose text matches.
    const aiByDate = this.aiForFeed(res.days, res.words, res.searching);
    const todayStub = this.aiTodayStubWanted(res.searching, aiByDate);
    const groups = groupByDate(res.rows) as { date: string; rows: EodReport[] }[];
    for (const d of [...aiByDate.keys(), ...(todayStub ? [this.today] : [])]) if (!groups.some(g => g.date === d)) groups.push({ date: d, rows: [] });
    groups.sort((a, b) => String(b.date).localeCompare(String(a.date)));

    let list: string;
    if (!this.reports.length && !groups.length) {
      list = `<div class="eod-empty">No EOD reports in the last ${RETENTION_DAYS} days — yours can be the first.</div>`;
    } else if (!groups.length) {
      const narrowed = res.searching || this.eodDept !== 'all' || this.eodMine;
      list = `<div class="eod-empty"><span>${narrowed ? 'No reports match.' : `No EOD reports in the last ${res.days} days.`}</span>
        <div class="eod-empty-acts">
          ${!res.searching && res.days < RETENTION_DAYS ? `<button type="button" class="btn btn-secondary btn-sm" data-eod-days="${RETENTION_DAYS}">Show all ${RETENTION_DAYS} days</button>` : ''}
          ${active ? `<button type="button" class="btn btn-ghost btn-sm" data-eod-clear>Clear filters</button>` : ''}
        </div></div>`;
    } else {
      // Collapsed by default: <details> per day — today open, earlier days
      // closed, a search opens everything. The header names who filed so a
      // closed day still reads at a glance.
      list = groups.map(g => {
        const ai = aiByDate.get(g.date) || null;
        const h = g.rows.length ? dayHeader(g, this.today) : { label: g.date === this.today ? 'Today' : fmtDay(g.date), count: 'no team reports yet', who: '' };
        const open = dayOpen(g.date, { today: this.today, searching: res.searching, overrides: this.eodDayToggles })
          || g.rows.some(r => r.id === this.editingReport);   // the day holding a live edit never folds away
        return `
          <details class="eod-day" data-eod-day="${esc(g.date)}" ${open ? 'open' : ''}>
            <summary class="eod-day-head"><span class="eod-min-caret">▶</span>${esc(h.label)}<span class="eod-meta">· ${esc(h.count)}${h.who ? ` · ${esc(h.who)}` : ''}${ai ? ' · 🤖 AI report' : ''}</span></summary>
            ${ai ? this.aiCard(ai, res.words, res.searching) : (g.date === this.today && todayStub ? this.aiTodayStub() : '')}
            ${g.rows.map(r => this.reportCard(r, res.words, res.searching)).join('')}
          </details>`;
      }).join('');
    }
    return { ctl, list };
  }

  private redrawFeed(): void {
    const ctl = this.container.querySelector('#eod-feed-ctl');
    const list = this.container.querySelector('#eod-feed');
    if (!ctl || !list) { this.draw(); return; }   // feed not on screen (another tab / still loading) — a full draw is the safe fallback
    // R115 fix (eod 6, defence in depth): an inline-edit field that had focus
    // gets it back — with its caret — after the swap (the text itself already
    // lives in editDraft)
    const ae = document.activeElement as HTMLTextAreaElement | null;
    const edAttr = ae && list.contains(ae) ? ['data-ed-summary', 'data-ed-blockers', 'data-ed-tomorrow'].find(a => ae.hasAttribute(a)) : undefined;
    const caret = edAttr && ae ? { a: ae.selectionStart, b: ae.selectionEnd, top: ae.scrollTop } : null;
    const parts = this.feedParts();
    closeKebab();   // a ⋯ menu open on a card that is about to be replaced
    ctl.innerHTML = parts.ctl;
    list.innerHTML = parts.list;
    this.bindFeed();
    if (edAttr && caret) {
      const ta = list.querySelector<HTMLTextAreaElement>(`[${edAttr}]`);
      if (ta) { ta.focus(); try { ta.setSelectionRange(caret.a ?? ta.value.length, caret.b ?? ta.value.length); } catch { /* not a text field */ } ta.scrollTop = caret.top; }
    }
  }
  // The user is in the middle of something inside the feed: an inline edit, an
  // open ⋯ menu (native details or the shared Kebab), or a focused field.
  private feedBusy(): boolean {
    const c = this.container;
    const feed = c.querySelector('#eod-feed');
    const ae = document.activeElement;
    return !!(this.editingReport
      || c.querySelector('details.eod-dd[open]')
      || c.querySelector('[data-kebab][aria-expanded="true"]')
      || (feed && ae && ae !== document.body && feed.contains(ae) && /^(TEXTAREA|INPUT|SELECT)$/.test(ae.tagName)));
  }

  // ── R115 C5: the AI end-of-day report ─────────────────────────────────────
  // Latest AI report per date inside the feed's window (the server already
  // answers latest-per-date). A department / Mine filter hides them; a search
  // keeps the ones whose text has every word.
  private aiForFeed(days: number, words: string[], searching: boolean): Map<string, AiReport> {
    const out = new Map<string, AiReport>();
    if (this.eodDept !== 'all' || this.eodMine || !this.aiReports.length) return out;
    const cutoff = this.today ? addDays(this.today, -days) : '';
    for (const r of this.aiReports) {
      if (!r || !r.date || (cutoff && r.date < cutoff) || out.has(r.date)) continue;
      if (searching) { const t = this.aiPlainText(r).toLowerCase(); if (!words.every(w => t.includes(w))) continue; }
      out.set(r.date, r);
    }
    return out;
  }
  // Today, before tonight's run: one quiet line saying when it runs.
  private aiTodayStubWanted(searching: boolean, ai: Map<string, AiReport>): boolean {
    return !!(this.aiStatus && this.today && !searching && this.eodDept === 'all' && !this.eodMine && !ai.has(this.today));
  }
  private aiTz(): string { const tz = this.aiStatus?.tz || ''; return /Los_Angeles/.test(tz) ? 'PT' : tz; }
  // "Runs nightly at 9:00 pm PT · last run Tue, Oct 13" — or why it doesn't.
  private aiSchedText(): string {
    const s = this.aiStatus;
    if (!s) return '';
    const last = !s.last_timer_run ? '' : s.last_timer_run === this.today ? ' · ran tonight' : ` · last run ${fmtDay(s.last_timer_run)}`;
    return s.armed ? `Runs nightly at ${s.hour_label} ${this.aiTz()}${last}` : `Nightly run is off on this server${last}`;
  }
  private aiTodayStub(): string {
    const s = this.aiStatus!;
    const esc = (x: string) => Formatters.escapeHtml(x);
    const running = s.running && s.running.date === this.today;
    const line = running ? 'writing tonight\'s report now…'
      : s.armed ? `runs tonight at ${s.hour_label} ${this.aiTz()}` : `not scheduled on this server${this.aiMoney() ? ' — use Generate now' : ''}`;
    return `<div class="eod-card eod-ai-stub" title="${esc(s.armed ? '' : (s.reason || ''))}"><span class="eod-ai-ic" aria-hidden="true">🤖</span><b>AI end-of-day</b><span class="eod-meta">· ${esc(line)}</span></div>`;
  }
  private aiPill(r: AiReport): string {
    const esc = (x: string) => Formatters.escapeHtml(x);
    const ai = r.ai || { status: 'pending' };
    if (r.narrative_source === 'claude') return '<span class="pill pill-blue">AI</span>';
    if (ai.status === 'pending') return '<span class="pill pill-gray" title="The facts-only summary is saved; the AI is still writing.">Writing…</span>';
    if (ai.status === 'skipped') return `<span class="pill pill-gray" title="${esc(ai.label || 'AI is not set up on this server')}">Facts only</span>`;
    const short = AI_KIND_SHORT[String(ai.kind || '')] || 'failed';
    return `<span class="pill pill-amber" title="${esc(ai.label || 'AI request failed')}">AI failed — ${esc(short)}</span>`;
  }
  // Copy text / search text: headline, sections, tomorrow, risks.
  private aiPlainText(r: AiReport): string {
    const n = r.narrative;
    if (!n) return '';
    const parts = [`AI end-of-day — ${fmtDay(r.date)}`, n.headline || ''];
    for (const s of n.sections || []) { parts.push('', s.title); for (const b of s.bullets || []) parts.push(`• ${b}`); }
    if ((n.tomorrow || []).length) { parts.push('', 'Tomorrow'); for (const b of n.tomorrow) parts.push(`• ${b}`); }
    if ((n.risks || []).length) { parts.push('', 'Risks'); for (const b of n.risks) parts.push(`• ${b}`); }
    return parts.join('\n');
  }
  // What fed the report — the card's Sources ⓘ (v1: dash stores + the synced
  // Hostaway tables; nothing live). One muted line, details on tap.
  private aiSourcesLine(r: AiReport): string {
    const s = r.sources || {};
    const st = this.aiStatus;
    const slack = st ? (st.slack.on && (st.slack.bot || st.slack.webhook) ? 'Slack ✓' : 'Slack — not routed') : 'Slack';
    const inbox = this.aiInboxState(r);
    return ['Dash', 'Hostaway sync', inbox === 'connected' ? `Admin inbox (${Number(s.inbox_n) || 0})` : inbox === 'ready' ? 'Admin inbox — ready, no pushes yet' : 'Admin inbox — not connected', s.quo ? 'Quo' : 'Quo — not connected', slack, 'PriceLabs — not read'].join(' · ');
  }
  // R115 fix (eod 5): "connected" = an Apps Script push landed in the last 7
  // days; "ready" = EOD_INBOX_SECRET is set but nothing has been pushed. A
  // record stored before the fix (no gmail_ready) counts as connected only
  // when it actually carried emails.
  private aiInboxState(r: AiReport): 'connected' | 'ready' | 'off' {
    const s = r.sources || {};
    const legacy = s.gmail_ready === undefined;
    if (s.gmail && (!legacy || Number(s.inbox_n) > 0)) return 'connected';
    const ready = legacy ? !!(this.aiStatus && this.aiStatus.inbox_ready) : !!s.gmail_ready;
    return ready ? 'ready' : 'off';
  }
  private aiSourcesHtml(r: AiReport): string {
    const s = r.sources || {};
    const st = this.aiStatus;
    const esc = (x: string) => Formatters.escapeHtml(x);
    const row = (ok: boolean, name: string, text: string) => `<li><span class="eod-ai-dot ${ok ? 'on' : ''}" aria-hidden="true"></span><b>${esc(name)}</b> — ${esc(text)}</li>`;
    const slackText = !st ? 'unknown'
      : st.slack.on && st.slack.channel && st.slack.bot ? 'posts to the channel picked in Settings → Slack notifications'
      : st.slack.on && st.slack.webhook ? 'posts to the legacy webhook channel (pick a channel in Settings → Slack notifications)'
      : 'not posting — switch on "Nightly AI end-of-day report" and pick a channel in Settings → Slack notifications';
    const inbox = this.aiInboxState(r);
    const inboxN = Number(s.inbox_n) || 0;
    const inboxText = inbox === 'connected' ? `${inboxN} email${inboxN === 1 ? '' : 's'} today — only the sender's domain and a topic (booking, review, payment…) reach the report; subjects stay in the office note list`
      : inbox === 'ready' ? 'ready (EOD_INBOX_SECRET is set) but no Apps Script push in the last 7 days — see the connector guide'
      : 'not connected — needs EOD_INBOX_SECRET on Railway and the Apps Script push';
    return `
      <div class="eod-ai-srcbox">
        <ul>
          ${row(true, 'Dash', 'turns, inspections, work orders, issues (with the Issues-board actions applied), tasks, supplies, checklists, guest recovery, payment flags, MTR leads')}
          ${row(true, 'Team', `${Number(s.team_reports_n) || 0} EOD report${Number(s.team_reports_n) === 1 ? '' : 's'} · ${Number(s.team_notes_n) || 0} note${Number(s.team_notes_n) === 1 ? '' : 's'} added for tonight`)}
          ${row(true, 'Hostaway', 'the nightly sync (arrivals, departures, reviews) — no live calls')}
          ${row(inbox === 'connected', 'Admin inbox', inboxText)}
          ${row(!!s.quo, 'Quo', s.quo ? 'missed calls + texts from the webhook' : 'not connected — needs the Quo key + webhook')}
          ${row(!!(st && st.slack.on), 'Slack', slackText)}
          ${row(false, 'PriceLabs', s.pricelabs_key ? 'key present, not read in v1' : 'not read (API key disabled)')}
          ${row(true, 'Meeting notes', 'paste them with ＋ Note for tonight')}
        </ul>
        <div class="eod-meta">No phone numbers, emails, links, door / wifi codes or money go into this report. People the dash knows (guests, tenants, staff) are named by first name only; admin email shows as sender + topic only.</div>
      </div>`;
  }
  private aiCard(r: AiReport, words: string[], searching: boolean): string {
    const esc = (x: string) => Formatters.escapeHtml(x);
    const hl = (x: string) => highlight(x, words, Formatters.escapeHtml);
    const id = esc(r.id);
    const n: AiNarrative = r.narrative || { headline: 'No summary stored', sections: [], tomorrow: [], risks: [] };
    const who = r.trigger === 'manual' ? ` · generated${r.by ? ` by ${esc(r.by)}` : ''}` : '';
    const time = this.fmtTime(r.generated_at);
    const open = searching || this.aiOpen.has(r.id);
    if (!open) {
      return `
        <div class="eod-card eod-report eod-report-min eod-ai-min" data-eod-ai-expand="${id}" role="button" tabindex="0" title="Expand the AI end-of-day report">
          <span class="eod-min-caret">▶</span><span class="eod-ai-ic" aria-hidden="true">🤖</span>
          <b>AI end-of-day</b><span class="eod-meta">· ${esc(time)}</span>${this.aiPill(r)}
          <span class="eod-min-sum">${hl(collapsedSummary(n.headline))}</span>
        </div>`;
    }
    const ai = r.ai || { status: 'pending' };
    const warn = ai.status === 'pending' ? 'The AI is still writing — this is the facts-only summary for now.'
      : ai.status === 'skipped' ? `AI narrative not available (${ai.label || 'AI is not set up on this server'}) — this is the facts-only summary.`
      : ai.status === 'failed' ? `AI narrative unavailable — ${ai.label || 'the request failed'}. This is the facts-only summary.` : '';
    const list = (xs: string[]) => `<ul>${xs.map(b => `<li>${hl(b)}</li>`).join('')}</ul>`;
    const sl = r.slack;
    const slackLine = !sl ? '' : sl.posted ? `Posted to Slack${sl.via === 'webhook' ? ' (webhook)' : ''}`
      : sl.skipped === 'not_configured' ? 'Slack not set up — not posted' : sl.skipped === 'off' ? 'Slack post is switched off in Settings'
      : sl.skipped === 'no_route' ? 'Slack: no channel picked — not posted' : `Slack post failed${sl.error ? ` — ${sl.error}` : ''}`;
    const money = this.aiMoney();
    const st = this.aiStatus;
    const cost = money ? [
      ai.cost_usd != null ? `$${Number(ai.cost_usd).toFixed(2)} this report` : '',
      st && st.cost_30d ? `$${Number(st.cost_30d.usd).toFixed(2)} last ${st.cost_30d.days} d` : '',
      ai.model || '',
    ].filter(Boolean).join(' · ') : '';
    const items: KebabItem[] = [
      ...(money ? [{ icon: '📣', label: 'Post to Slack again', attrs: { 'data-eod-ai-slack': r.id } } as KebabItem] : []),
      { icon: '📋', label: 'Copy text', attrs: { 'data-eod-ai-copy': r.id } },
      { icon: '🔎', label: this.aiFactsOpen.has(r.id) ? 'Hide facts' : 'Show facts', attrs: { 'data-eod-ai-facts': r.id } },
    ];
    const facts = this.aiFactsOpen.has(r.id)
      ? `<details class="eod-ai-facts" open><summary>Facts the report was written from</summary><pre>${esc(this.aiFacts.has(r.id) ? JSON.stringify(this.aiFacts.get(r.id), null, 2) : 'Loading…')}</pre></details>` : '';
    const flash = this.aiFlash && this.aiFlash.id === r.id ? `<div class="eod-ai-flash" role="status">${esc(this.aiFlash.text)}</div>` : '';
    return `
      <div class="eod-card eod-report eod-ai" data-eod-ai-card="${id}">
        <div class="eod-card-head ${searching ? '' : 'eod-head-click'}" ${searching ? '' : `data-eod-ai-collapse="${id}" title="Collapse"`}>
          <div class="eod-head-main">
            ${searching ? '' : '<span class="eod-min-caret open">▶</span>'}<span class="eod-ai-ic" aria-hidden="true">🤖</span><strong>AI end-of-day</strong>
            <span class="eod-meta">${esc(time)}${who}</span>${this.aiPill(r)}
          </div>
          ${kebabHtml(items, { tip: 'AI report actions' })}
        </div>
        <div class="eod-ai-body">
          <div class="eod-ai-headline">${hl(n.headline)}</div>
          ${warn ? `<div class="eod-ai-warn">${esc(warn)}</div>` : ''}
          ${(n.sections || []).map(s => `<div class="eod-ai-sec"><div class="eod-ai-sec-t">${esc(s.title)}</div>${list(s.bullets || [])}</div>`).join('')}
          ${(n.tomorrow || []).length ? `<div class="eod-ai-sec"><div class="eod-ai-sec-t"><span class="pill pill-gray">Tomorrow</span></div>${list(n.tomorrow)}</div>` : ''}
          ${(n.risks || []).length ? `<div class="eod-ai-sec"><div class="eod-ai-sec-t"><span class="pill pill-amber">Risks</span></div>${list(n.risks)}</div>` : ''}
        </div>
        <div class="eod-ai-foot">
          <div class="eod-ai-srcline"><button type="button" class="eod-ai-srcbtn" data-eod-ai-src="${id}" aria-expanded="${this.aiSrcOpen.has(r.id)}" title="What this report was built from">Sources ⓘ</button><span class="eod-meta">${esc(this.aiSourcesLine(r))}</span></div>
          ${this.aiSrcOpen.has(r.id) ? this.aiSourcesHtml(r) : ''}
          ${r.date === this.today && st ? `<div class="eod-meta">${esc(this.aiSchedText())}</div>` : ''}
          ${slackLine ? `<div class="eod-meta">${esc(slackLine)}</div>` : ''}
          ${cost ? `<div class="eod-meta eod-ai-cost">${esc(cost)}</div>` : ''}
          ${flash}
        </div>
        ${facts}
      </div>`;
  }

  // The ONE row above the composer: Generate now (admin / director) + a note
  // for tonight's report (office roles), the notes already added today under
  // it. Field roles never see it (they read the cards).
  // R115 fix (eod 1): once tonight's timer report has run, a note / report
  // filed now goes into TOMORROW night's report (the server carries it) —
  // the bar says so instead of promising "tonight".
  // R115 fix-2 (D2): the server's note queue decides (the same rule the run
  // uses); the job status is the fallback for an older server.
  private aiRanTonight(): boolean {
    if (this.aiNotesRan != null) return this.aiNotesRan;
    const s = this.aiStatus;
    return !!(s && s.last_timer_run && this.today && s.last_timer_run === this.today);
  }
  private aiBar() {
    return aiNoteBar({ ran_tonight: this.aiRanTonight(), notes: this.aiNotes, included: this.aiNotesUsed });
  }
  private aiBarHtml(): string {
    const office = this.aiOffice(), money = this.aiMoney();
    if (!office || !this.aiStatus) return '';
    const esc = (x: string) => Formatters.escapeHtml(x);
    const bar = this.aiBar();
    const ran = bar.ran, which = bar.which;
    const me = this.actorEmail();
    const noteRow = (nt: AiNote & { tag: string }, used: boolean) => {
      const mine = !!me && nt.by_email === me;
      const canDel = money || (mine && nt.source === 'note');
      const head = nt.source === 'gmail' ? `✉️ ${nt.from_domain || 'email'}${nt.subject ? ` — ${nt.subject}` : ''}` : nt.source === 'meeting-notes' ? '🗒 Meeting notes' : `📝 ${nt.by || 'note'}`;
      return `<div class="eod-ai-note${used ? ' used' : ''}"><span class="eod-ai-note-txt"><b>${esc(head)}</b> ${esc(nt.text)}${nt.tag ? ` <span class="eod-meta">· ${esc(nt.tag)}</span>` : ''}</span>${canDel ? `<button type="button" class="eod-ai-x" data-eod-ai-note-del="${esc(nt.id)}" title="${used ? 'Delete this note' : 'Remove this note'}" aria-label="${used ? 'Delete note' : 'Remove note'}">×</button>` : ''}</div>`;
    };
    const notes = bar.groups.map(g => (g.key === 'included'
      ? `<div class="eod-ai-notes-sub">${esc(g.label)}</div>${g.notes.map(nt => noteRow(nt, true)).join('')}`
      : g.notes.map(nt => noteRow(nt, false)).join(''))).join('');
    return `
      <div class="eod-card eod-ai-bar">
        <div class="eod-ai-bar-row">
          <div class="eod-ai-bar-main"><b>${esc(bar.heading)}</b><span class="eod-meta">${esc(this.aiSchedText())}${bar.count ? ` · ${bar.count} note${bar.count === 1 ? '' : 's'} added` : ''}</span></div>
          <div class="eod-ai-bar-acts">
            ${office && !this.aiNoteOpen ? `<button type="button" class="btn btn-ghost btn-sm" data-eod-ai-note-open title="${esc(`Add something ${which} report should know`)}">＋ Note</button>` : ''}
            ${money ? `<button type="button" class="btn btn-secondary btn-sm" data-eod-ai-gen ${this.aiGenerating ? 'disabled' : ''} title="Build today's report now (adds a new version; the page shows the latest)">${this.aiGenerating ? 'Generating…' : 'Generate now'}</button>` : ''}
          </div>
        </div>
        ${this.aiGenMsg ? `<div class="eod-ai-msg" role="status">${esc(this.aiGenMsg)}</div>` : ''}
        ${this.aiNoteOpen ? `
          <div class="eod-form eod-ai-noteform">
            <textarea id="eod-ai-note" rows="3" maxlength="2000" placeholder="${esc(ran ? "Tonight's report already ran — this goes into tomorrow night's: a meeting decision, a heads-up…" : "Anything tonight's report should know — a meeting decision, an owner call, a heads-up for tomorrow…")}">${esc(this.aiNoteDraft)}</textarea>
            <div class="eod-meta">The whole team, field staff included, reads this report.</div>
            <div class="eod-form-acts">
              <button type="button" class="btn btn-ghost btn-sm" data-eod-ai-note-cancel>Cancel</button>
              <button type="button" class="btn btn-primary btn-sm" data-eod-ai-note-save ${this.aiNoteSaving ? 'disabled' : ''}>${this.aiNoteSaving ? 'Saving…' : 'Save note'}</button>
            </div>
          </div>` : ''}
        ${notes ? `<div class="eod-ai-notes">${notes}</div>` : ''}
      </div>`;
  }
  private redrawAiBar(): void {
    const host = this.container.querySelector<HTMLElement>('#eod-ai-bar');
    if (!host) return;
    host.innerHTML = this.aiBarHtml();
    this.bindAiBar();
  }
  private bindAiBar(): void {
    const c = this.container;
    const host = c.querySelector<HTMLElement>('#eod-ai-bar');
    if (!host) return;
    host.querySelector('[data-eod-ai-gen]')?.addEventListener('click', () => { void this.aiGenerate(); });
    host.querySelector('[data-eod-ai-note-open]')?.addEventListener('click', () => {
      this.aiNoteOpen = true; this.redrawAiBar();
      host.querySelector<HTMLTextAreaElement>('#eod-ai-note')?.focus();
    });
    host.querySelector<HTMLTextAreaElement>('#eod-ai-note')?.addEventListener('input', (e) => { this.aiNoteDraft = (e.target as HTMLTextAreaElement).value; });
    host.querySelector('[data-eod-ai-note-cancel]')?.addEventListener('click', () => {
      if (this.aiNoteDraft.trim() && !confirm('Discard this note?')) return;
      this.aiNoteOpen = false; this.aiNoteDraft = ''; this.redrawAiBar();
    });
    host.querySelector('[data-eod-ai-note-save]')?.addEventListener('click', async () => {
      const text = this.aiNoteDraft.trim();
      if (!text) { alert('Write the note first.'); return; }
      this.aiNoteSaving = true; this.redrawAiBar();
      try {
        const r = await fetch(`${API}/eod/ai/notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text, by: this.actorName() || 'unknown', by_email: this.actorEmail() }) });
        const j = await r.json().catch(() => null);
        if (!r.ok) throw new Error((j && j.error) || `HTTP ${r.status}`);
        if (j && j.note) this.aiNotes.push(j.note);
        this.aiNoteOpen = false; this.aiNoteDraft = '';
      } catch (e: any) { alert(`Couldn't save the note — ${e.message}. Your text is still in the box.`); }
      this.aiNoteSaving = false; this.redrawAiBar();
    });
    host.querySelectorAll<HTMLElement>('[data-eod-ai-note-del]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.eodAiNoteDel!;
      if (!confirm(aiNoteConfirm(this.aiBar(), id) || `Remove this note from ${this.aiBar().which} report?`)) return;
      try {
        const r = await fetch(`${API}/eod/ai/notes/${encodeURIComponent(id)}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actor_email: this.actorEmail() }) });   // the server decides by the session
        const j = await r.json().catch(() => null);
        if (!r.ok) throw new Error((j && j.error) || `HTTP ${r.status}`);
        this.aiNotes = this.aiNotes.filter(x => x.id !== id);
        this.aiNotesUsed = this.aiNotesUsed.filter(x => x.id !== id);
        this.redrawAiBar();
      } catch (e: any) { alert(`Couldn't remove it — ${e.message}`); }
    }));
  }
  // Generate now: the server answers 202 and works in the background (the
  // facts-only record lands first, then the AI narrative) — poll GET /eod/ai
  // every 3 s for up to 4 min. A second press while one runs answers 409.
  private async aiGenerate(): Promise<void> {
    if (this.aiGenerating) return;
    this.aiGenerating = true;
    this.aiGenMsg = 'Generating — the facts are saved first, then the AI writes (usually under two minutes).';
    this.redrawAiBar();
    let timedOut = false;
    try {
      const r = await fetch(`${API}/eod/ai/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ by_email: this.actorEmail() }) });
      const j = await r.json().catch(() => null);
      if (r.status === 409) this.aiGenMsg = 'A report is already being generated — it will show up here shortly.';
      else if (!r.ok) throw new Error((j && j.error) || `HTTP ${r.status}`);
      const deadline = Date.now() + 4 * 60000;
      // R115 fix (eod 6): redraw only when today's AI card visibly changed and
      // the user is not busy in the feed (an inline edit lost focus + caret,
      // and open ⋯ menus snapped shut, on every 3 s poll); expand the card
      // only when it lands / changes (a user who collapsed it keeps it shut)
      const sig = () => aiPollSig(this.aiReports, this.today, this.aiStatus);
      const onScreen = () => !!this.container.querySelector('#eod-feed');   // never redrawFeed's full-draw fallback (a checklist edit on another tab)
      let drawn = sig(), seen = drawn;
      const step = () => {
        const now = sig();
        if (now !== seen) { const mine = this.aiReports.find(x => x.date === this.today); if (mine) this.aiOpen.add(mine.id); seen = now; }
        if (aiPollRedraw({ drawn, now, busy: this.feedBusy(), onScreen: onScreen() })) { this.redrawFeed(); drawn = now; }
      };
      for (;;) {
        await new Promise(res => setTimeout(res, 3000));
        await this.loadAi();
        step();
        if (this.aiStatus && !this.aiStatus.running) break;
        if (Date.now() > deadline) { timedOut = true; break; }
      }
      step();   // the catch-up: a change skipped while the user was busy is drawn now if they are idle (else on their next redraw)
      this.aiGenMsg = timedOut ? 'Still working — the report will appear when it finishes (reload in a minute).' : '';
      await this.loadAiNotes();   // the run moved the cutoff: notes it read are now "already in tonight's report" (fix-2 D2)
    } catch (e: any) {
      this.aiGenMsg = `Couldn't start the report — ${e.message}`;
    }
    this.aiGenerating = false;
    this.redrawAiBar();
  }

  // R113 #6 (G): one thumb strip per report, every thumb a button carrying its
  // url — a tap opens the shared Lightbox on that index (prev/next, keys,
  // swipe, video), instead of target=_blank tabs.
  private photoStrip(urls: string[]): string {
    if (!urls.length) return '';
    const esc = (s: string) => Formatters.escapeHtml(s);
    return `<div class="eod-photos" data-strip>${urls.map((u, i) => {
      const vid = isVideo(u);
      return `<button type="button" class="eod-thumb" data-eod-media="${esc(u)}" title="${vid ? 'Play video' : 'View photo'}" aria-label="${vid ? 'Video' : 'Photo'} ${i + 1} of ${urls.length}">${vid
        ? `<video src="${esc(u)}" preload="metadata" muted playsinline></video><span class="eod-play">▶</span>`
        : `<img src="${esc(u)}" alt="" loading="lazy">`}</button>`;
    }).join('')}</div>`;
  }

  // One report card — collapsed one-liner (R110 #3), read view, or inline edit
  // view (R29). Every edit is shown: "✎ edited by X · time · fields" lines
  // accumulate under the report.
  private reportCard(r: EodReport, words: string[] = [], searching = false): string {
    const t = (iso: string) => this.fmtTime(iso);
    const hl = (s: string) => highlight(s, words, Formatters.escapeHtml);   // escape first, then <mark> the search words
    const id = Formatters.escapeHtml(r.id);
    const photoList = r.photos || [];
    if (!reportOpen(r.id, { open: this.eodOpen, editing: this.editingReport, searching })) {
      // Collapsed: person · department · time · first ~110 chars · 📷 n. A
      // Blockers tag survives collapsing — it's the line a reader scans for.
      const n = photoList.length;
      return `
        <div class="eod-card eod-report eod-report-min" data-eod-expand="${id}" role="button" tabindex="0" title="Expand">
          <span class="eod-min-caret">▶</span>
          <b>${hl(r.person)}</b>
          ${r.department ? `<span class="eod-meta">· ${hl(r.department)}</span>` : ''}
          <span class="eod-meta">· ${t(r.at)}</span>
          <span class="eod-min-sum">${hl(collapsedSummary(r.summary))}</span>
          ${r.blockers ? `<span class="pill pill-amber" title="${Formatters.escapeHtml(collapsedSummary(r.blockers, 160))}">Blockers</span>` : ''}
          ${n ? `<span class="eod-meta" title="${n} attachment${n === 1 ? '' : 's'}">📷 ${n}</span>` : ''}
        </div>`;
    }
    const photos = this.photoStrip(photoList);
    const trail = (r.edits || []).map(e =>
      `<div class="eod-edit-line">✎ edited by <b>${Formatters.escapeHtml(e.by)}</b> · ${t(e.at)} · ${Formatters.escapeHtml((e.fields || []).join(', '))}</div>`).join('');
    if (this.editingReport === r.id) {
      const d = this.editDraft && this.editDraft.id === r.id ? this.editDraft : { summary: r.summary, blockers: r.blockers || '', tomorrow: r.tomorrow || '' };
      return `
        <div class="eod-card eod-report">
          <div class="eod-card-head">
            <div class="eod-head-main"><strong>${Formatters.escapeHtml(r.person)}</strong><span class="eod-meta">editing — the edit will be logged under your name</span></div>
          </div>
          <div class="eod-form">
            <textarea data-ed-summary rows="4" aria-label="Summary">${Formatters.escapeHtml(d.summary)}</textarea>
            <textarea data-ed-blockers rows="2" placeholder="Blockers (optional)…" aria-label="Blockers">${Formatters.escapeHtml(d.blockers)}</textarea>
            <textarea data-ed-tomorrow rows="2" placeholder="Tomorrow (optional)…" aria-label="Tomorrow">${Formatters.escapeHtml(d.tomorrow)}</textarea>
            <div class="eod-form-acts">
              <button type="button" class="btn btn-secondary" data-eod-cancel>Cancel</button>
              <button type="button" class="btn btn-primary" data-eod-save="${id}">Save edit</button>
            </div>
          </div>
          ${photos}
          ${trail}
        </div>`;
    }
    // R48 (Kai): edit/photo controls only on YOUR OWN report — people were
    // accidentally overwriting each other's notes. Admin/director can edit any
    // (matches the role model); legacy 'unknown'-author rows stay open.
    const me = (this.actorName() || '').trim().toLowerCase();
    const myEmail = this.actorEmail();
    const author = String(r.person || '').trim().toLowerCase();
    const authorEmail = String(r.person_email || '').trim().toLowerCase();
    const role = this.actorRole();
    const canEdit = !author || author === 'unknown' || author === me
      || (!!authorEmail && !!myEmail && authorEmail === myEmail)
      || ['admin', 'director'].includes(role);
    // R110 #3: the head collapses the card; R113 #6: its only control is ONE
    // ⋯ menu on the right (Edit report / Add photos) — see bindFeed. While a
    // search is active every match stays open, so no collapse affordance then.
    const collapsible = !searching;
    return `
      <div class="eod-card eod-report">
        <div class="eod-card-head ${collapsible ? 'eod-head-click' : ''}" ${collapsible ? `data-eod-collapse="${id}" title="Collapse"` : ''}>
          <div class="eod-head-main">
            ${collapsible ? '<span class="eod-min-caret open">▶</span>' : ''}<strong>${hl(r.person)}</strong>
            <span class="eod-meta">${r.department ? `${hl(r.department)} · ` : ''}${t(r.at)}</span>
          </div>
          ${canEdit ? `
          <details class="eod-dd" data-eod-menu="${id}">
            <summary class="eod-ibtn" title="More" aria-label="Report actions">⋯</summary>
            <div class="eod-dd-menu" role="menu">
              <button type="button" class="eod-dd-item" role="menuitem" data-eod-edit="${id}">Edit report</button>
              <label class="eod-dd-item" role="menuitem">Add photos / video<input type="file" accept="image/*,video/*" multiple data-eod-photo="${id}" style="display:none;"></label>
            </div>
          </details>` : ''}
        </div>
        <div class="eod-report-body">${hl(r.summary)}</div>
        ${r.blockers ? `<div class="eod-report-sec"><span class="pill pill-amber">Blockers</span> ${hl(r.blockers)}</div>` : ''}
        ${r.tomorrow ? `<div class="eod-report-sec"><span class="pill pill-gray">Tomorrow</span> ${hl(r.tomorrow)}</div>` : ''}
        ${photos}
        ${trail}
      </div>`;
  }

  // ── bindings ───────────────────────────────────────────────────────────────
  private bind(): void {
    const c = this.container;
    c.querySelectorAll<HTMLElement>('[data-eod-tab]').forEach(b => b.addEventListener('click', () => {
      this.tab = b.dataset.eodTab as typeof this.tab;
      this.draw();
    }));

    // R34: retry a failed checklist load in place
    c.querySelector('[data-eod-retry]')?.addEventListener('click', () => { void this.render(); });

    // R30 #7: Staff Schedule tab hosts the Team roster (remote hours + ground
    // on-call) right here on Daily Ops — same data as Remote Ops → Team.
    if (this.tab === 'schedule') {
      const host = c.querySelector('#eod-body') as HTMLElement | null;
      if (host) void new TeamPage(host).render();
    }

    c.querySelector('#eod-date')?.addEventListener('change', async e => {
      const prev = this.date;
      this.date = (e.target as HTMLInputElement).value || this.today;
      this.loading = true; this.draw();
      await this.loadChecklists();
      // R34: a failed load must not show the OLD day's checks under the NEW
      // date — snap back so the date and the data always agree.
      if (this.loadFailed && prev) this.date = prev;
      this.loading = false; this.draw();
    });
    c.querySelector('#eod-today-btn')?.addEventListener('click', async () => {
      this.date = this.today;
      this.loading = true; this.draw();
      await this.loadChecklists();
      this.loading = false; this.draw();
    });
    c.querySelector('#eod-manage-btn')?.addEventListener('click', () => { this.manage = true; this.editTpl = null; this.draw(); });
    c.querySelector('#eod-manage-back')?.addEventListener('click', () => { this.manage = false; this.editTpl = null; this.draw(); });

    // R30: one splice path for every item action. The stale guard compares the
    // echoed view_date (weekly/monthly runs carry their ANCHOR as run.date).
    const spliceRun = (j: any, tplId: string): boolean => {
      if (!j || !j.run) return false;
      if ((j.view_date || j.run.date) !== this.date) return false;
      const ix = this.runs.findIndex(x => x.template_id === tplId);
      if (ix >= 0) this.runs[ix] = j.run; else this.runs.push(j.run);
      return true;
    };
    const postCheck = async (tplId: string, body: Record<string, unknown>): Promise<any> => {
      const r = await fetch(`${API}/daily-checklists/check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: tplId, by: this.actorName(), date: this.date, ...body }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    };
    // check/uncheck an item — optimistic, revert on failure
    c.querySelectorAll<HTMLInputElement>('[data-check]').forEach(cb => cb.addEventListener('change', async () => {
      const [tplId, itemId] = cb.dataset.check!.split('|');
      const done = cb.checked;
      try {
        const j = await postCheck(tplId, { item_id: itemId, done });
        if (spliceRun(j, tplId) && this.tab === 'checklists') this.draw();
      } catch { cb.checked = !done; }
    }));
    // N/A toggle (clears done; mutually exclusive server-side)
    c.querySelectorAll<HTMLElement>('[data-na]').forEach(btn => btn.addEventListener('click', async () => {
      const [tplId, itemId, to] = btn.dataset.na!.split('|');
      try {
        const j = await postCheck(tplId, { item_id: itemId, na: to === '1' });
        if (spliceRun(j, tplId) && this.tab === 'checklists') this.draw();
      } catch (e: any) { alert(`Could not update — ${e.message}`); }
    }));
    // verification note
    c.querySelectorAll<HTMLElement>('[data-note]').forEach(btn => btn.addEventListener('click', async () => {
      const [tplId, itemId] = btn.dataset.note!.split('|');
      const run = this.runs.find(x => x.template_id === tplId);
      const cur = run?.checks?.[itemId]?.note || '';
      const note = prompt('Verification note for this item:', cur);
      if (note == null) return;
      try {
        const j = await postCheck(tplId, { item_id: itemId, note });
        if (spliceRun(j, tplId) && this.tab === 'checklists') this.draw();
      } catch (e: any) { alert(`Could not save note — ${e.message}`); }
    }));
    // screenshot/photo verification (batched — server caps 6 files/request)
    c.querySelectorAll<HTMLInputElement>('[data-item-photo]').forEach(inp => inp.addEventListener('change', async (e) => {
      const el = e.target as HTMLInputElement;
      const [tplId, itemId] = el.dataset.itemPhoto!.split('|');
      const files = Array.from(el.files || []);
      el.value = '';
      if (!files.length) return;
      try {
        let last: any = null;
        for (let i = 0; i < files.length; i += 6) {
          const fd = new FormData();
          fd.append('template_id', tplId);
          fd.append('item_id', itemId);
          fd.append('date', this.date);
          fd.append('by', this.actorName());
          for (const f of files.slice(i, i + 6)) fd.append('photo', f, f.name);
          const r = await fetch(`${API}/daily-checklists/photo`, { method: 'POST', body: fd });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          last = await r.json().catch(() => null);
        }
        if (last && spliceRun(last, tplId) && this.tab === 'checklists') this.draw();
      } catch (err: any) { alert(`Photo upload failed — ${err.message}`); }
    }));

    // template editor
    c.querySelectorAll<HTMLElement>('[data-tpl-edit]').forEach(b => b.addEventListener('click', () => {
      this.editTpl = this.templates.find(t => t.id === b.dataset.tplEdit) || null;
      this.draw();
    }));
    c.querySelector('#tpl-cancel')?.addEventListener('click', () => { this.editTpl = null; this.draw(); });
    c.querySelectorAll<HTMLElement>('[data-tpl-del]').forEach(b => b.addEventListener('click', async () => {
      const t = this.templates.find(x => x.id === b.dataset.tplDel);
      if (!t || !confirm(`Delete "${t.name}"? Its daily history stays but no new runs are created.`)) return;
      try {
        const r = await fetch(`${API}/daily-checklists/templates/${encodeURIComponent(t.id)}`, { method: 'DELETE' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        this.templates = j.templates;
        this.draw();
      } catch (e: any) { alert(`Delete failed — ${e.message}`); }
    }));
    c.querySelector('#tpl-save')?.addEventListener('click', async e => {
      if (this.tplSaving) return;   // double-click would create duplicate templates
      const btn = e.currentTarget as HTMLButtonElement;
      const val = (id: string) => (c.querySelector(`#${id}`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null)?.value ?? '';
      const name = val('tpl-name').trim();
      const items = val('tpl-items').split('\n').map(s => s.trim()).filter(Boolean);
      if (!name) { alert('Give the checklist a name.'); return; }
      if (!items.length) { alert('Add at least one check (one per line).'); return; }
      this.tplSaving = true;
      btn.disabled = true; btn.textContent = 'Saving…';
      try {
        const r = await fetch(`${API}/daily-checklists/templates`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: this.editTpl?.id, name, department: val('tpl-dept'), cadence: val('tpl-cadence'), assignee: val('tpl-assignee').trim(), items }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        this.templates = j.templates;
        this.editTpl = null;
        this.tplSaving = false;
        await this.loadChecklists();   // pick up the new run for today
        this.draw();
      } catch (e2: any) {
        alert(`Save failed — ${e2.message}`);
        this.tplSaving = false;
        btn.disabled = false; btn.textContent = this.editTpl?.id ? 'Save changes' : 'Create checklist';
      }
    });

    // EOD composer — keep the draft on failure so nothing typed is lost
    const snapshotDraft = () => {
      const val = (id: string) => (c.querySelector(`#${id}`) as HTMLTextAreaElement | HTMLSelectElement | null)?.value ?? '';
      this.eodDraft = { summary: val('eod-summary'), blockers: val('eod-blockers'), tomorrow: val('eod-tomorrow'), department: val('eod-dept') };
    };
    // R113 #6: .open shows the extra fields. Toggled on the DOM (no redraw) so
    // the caret never jumps: open on summary focus or any text; close once
    // focus leaves the whole composer AND nothing is typed. The deferred
    // check lets a click on the department select land before deciding.
    const comp = c.querySelector<HTMLElement>('[data-eod-composer]');
    const summaryTa = c.querySelector<HTMLTextAreaElement>('#eod-summary');
    const openComposer = () => {
      if (!comp || comp.classList.contains('open')) return;
      comp.classList.add('open');
      if (summaryTa) summaryTa.rows = 4;
    };
    const maybeCloseComposer = () => {
      window.setTimeout(() => {
        if (!comp || !comp.isConnected) return;
        const a = document.activeElement;
        if (a && comp.contains(a)) return;
        if (this.draftHasText()) return;
        comp.classList.remove('open');
        if (summaryTa) summaryTa.rows = 2;
      }, 0);
    };
    summaryTa?.addEventListener('focus', openComposer);
    comp?.addEventListener('focusout', maybeCloseComposer);
    c.querySelectorAll('#eod-summary, #eod-blockers, #eod-tomorrow, #eod-dept').forEach(el => {
      el.addEventListener('input', () => { snapshotDraft(); if (this.draftHasText()) openComposer(); });
      el.addEventListener('change', snapshotDraft);
    });
    c.querySelector('[data-eod-add]')?.addEventListener('click', () => {
      this.composerAdd = true;
      this.draw();
      c.querySelector<HTMLTextAreaElement>('#eod-summary')?.focus();
    });
    c.querySelector<HTMLInputElement>('#eod-photos')?.addEventListener('change', (e) => {
      const inp = e.target as HTMLInputElement;
      for (const f of Array.from(inp.files || [])) if (this.eodFiles.length < 20) this.eodFiles.push(f);
      inp.value = '';
      snapshotDraft();
      this.draw();
    });
    c.querySelector('#eod-submit')?.addEventListener('click', async () => {
      snapshotDraft();
      if (!this.eodDraft.summary.trim()) { alert('Write a quick summary of your day first.'); return; }
      this.eodPending = true; this.draw();
      try {
        const r = await fetch(`${API}/eod`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ person: this.actorName() || 'unknown', person_email: this.actorEmail(), ...this.eodDraft }),
        });
        const j = await r.json().catch(() => null);
        if (!r.ok) throw new Error((j && j.error) || `HTTP ${r.status}`);
        if (j?.report?.id && this.eodFiles.length) {
          try { await this.uploadEodPhotos(j.report.id, this.eodFiles); }
          catch (pe: any) { alert(`Report submitted, but photos failed — ${pe.message}. Use ⋯ → Add photos on the report to retry.`); }
        }
        this.eodDraft = { summary: '', blockers: '', tomorrow: '', department: '' };
        this.eodFiles = [];
        this.composerAdd = false;
        await this.loadEod();
      } catch (e: any) { alert(`Submit failed — ${e.message}. Your text is still in the form.`); }
      this.eodPending = false;
      this.draw();
    });

    // R110 #3: the search box lives OUTSIDE the feed region so typing only
    // redraws the feed (redrawFeed) — the input and its caret are never
    // replaced. Debounced a touch; Esc and the native ✕ clear it.
    const q = c.querySelector<HTMLInputElement>('#eod-q');
    if (q) {
      let timer = 0;
      const apply = () => { window.clearTimeout(timer); this.eodQ = q.value; this.redrawFeed(); };
      q.addEventListener('input', () => { window.clearTimeout(timer); timer = window.setTimeout(apply, 120); });
      q.addEventListener('search', apply);
      q.addEventListener('keydown', e => { if (e.key === 'Escape') { q.value = ''; apply(); } });
    }
    this.bindFeed();
    this.bindAiBar();   // R115 C5
  }

  // R110 #3: everything inside #eod-feed-ctl / #eod-feed — rebound after every
  // redrawFeed(). The R29 edit flow and per-report photo add moved here from
  // bind() because the cards they live on now re-render without a full draw.
  private bindFeed(): void {
    const c = this.container;
    bindKebabs(c);   // R115: the AI card's ⋯ (one delegated opener per root)
    this.bindAiCards();
    // filters
    c.querySelectorAll<HTMLElement>('[data-eod-days]').forEach(b => b.addEventListener('click', () => { this.eodDays = Number(b.dataset.eodDays) || DEFAULT_WINDOW; this.redrawFeed(); }));
    // R113 #6: the department filter is a <select> (value = department | all);
    // a button carrying data-eod-dept still works as a chip would.
    c.querySelectorAll<HTMLElement>('[data-eod-dept]').forEach(el => {
      if (el instanceof HTMLSelectElement) el.addEventListener('change', () => { this.eodDept = el.value || 'all'; this.redrawFeed(); });
      else el.addEventListener('click', () => { this.eodDept = el.dataset.eodDept || 'all'; this.redrawFeed(); });
    });
    c.querySelectorAll<HTMLElement>('[data-eod-mine]').forEach(b => b.addEventListener('click', () => { this.eodMine = !this.eodMine; this.redrawFeed(); }));
    c.querySelectorAll<HTMLElement>('[data-eod-clear]').forEach(b => b.addEventListener('click', () => {
      this.eodQ = ''; this.eodDept = 'all'; this.eodMine = false; this.eodDays = DEFAULT_WINDOW;
      const inp = c.querySelector<HTMLInputElement>('#eod-q');
      if (inp) inp.value = '';
      this.redrawFeed();
    }));
    // day groups: remember the user's own toggles so a redraw doesn't undo
    // them (ignored while a search forces every day open)
    c.querySelectorAll<HTMLDetailsElement>('details[data-eod-day]').forEach(d => d.addEventListener('toggle', () => {
      if (!d.isConnected || this.eodSearching()) return;
      this.eodDayToggles.set(d.dataset.eodDay!, d.open);
    }));
    // expand / collapse one report
    c.querySelectorAll<HTMLElement>('[data-eod-expand]').forEach(el => {
      const open = () => { this.eodOpen.add(el.dataset.eodExpand!); this.redrawFeed(); };
      el.addEventListener('click', open);
      el.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    });
    c.querySelectorAll<HTMLElement>('[data-eod-collapse]').forEach(el => el.addEventListener('click', e => {
      // the head also hosts the ⋯ menu (a <details>) — its summary and items act, they don't collapse
      if ((e.target as HTMLElement).closest('[data-eod-edit], label, input, a, button, summary, details')) return;
      this.eodOpen.delete(el.dataset.eodCollapse!);
      this.redrawFeed();
    }));
    // R113 #6: the ⋯ menu is a native <details>; one open at a time, and it
    // closes on an outside tap or Esc (listeners attach while open and drop
    // themselves once it closes or the card is redrawn from under it).
    c.querySelectorAll<HTMLDetailsElement>('details.eod-dd').forEach(d => d.addEventListener('toggle', () => {
      if (!d.open) return;
      c.querySelectorAll<HTMLDetailsElement>('details.eod-dd[open]').forEach(o => { if (o !== d) o.open = false; });
      const drop = () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey, true); };
      const onDown = (ev: Event) => {
        if (!d.isConnected) { drop(); return; }
        if (d.contains(ev.target as Node)) return;
        d.open = false; drop();
      };
      const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') { d.open = false; drop(); } };
      document.addEventListener('pointerdown', onDown, true);
      document.addEventListener('keydown', onKey, true);
    }));
    // R113 #6 (G): thumbs open the shared Lightbox at the tapped index
    c.querySelectorAll<HTMLElement>('[data-eod-media]').forEach(el => el.addEventListener('click', () => {
      const { urls, index } = stripUrlsFor(el, 'data-eod-media');
      openLightbox(urls, index);
    }));

    // R29: edit flow — open, save (logged under the editor's name), cancel;
    // plus per-report photo add from the ⋯ menu.
    c.querySelectorAll<HTMLElement>('[data-eod-edit]').forEach(el => el.addEventListener('click', () => {
      const id = el.dataset.eodEdit!;
      if (this.editingReport && this.editingReport !== id && this.editDirty() && !confirm('Discard the unsaved edit on the other report?')) return;
      const r = this.reports.find(x => x.id === id);
      this.editingReport = id;
      this.editDraft = { id, summary: r?.summary || '', blockers: r?.blockers || '', tomorrow: r?.tomorrow || '' };
      this.eodOpen.add(this.editingReport);   // R110 #3: still expanded after the edit is saved or cancelled
      this.redrawFeed();
    }));
    // what's typed is kept on the instance, so a chip click, search keystroke
    // or expand/collapse elsewhere (all redraw the feed) never reverts it
    c.querySelectorAll<HTMLTextAreaElement>('[data-ed-summary], [data-ed-blockers], [data-ed-tomorrow]').forEach(ta => ta.addEventListener('input', () => {
      if (!this.editDraft || this.editDraft.id !== this.editingReport) return;
      if (ta.hasAttribute('data-ed-summary')) this.editDraft.summary = ta.value;
      else if (ta.hasAttribute('data-ed-blockers')) this.editDraft.blockers = ta.value;
      else this.editDraft.tomorrow = ta.value;
    }));
    c.querySelector('[data-eod-cancel]')?.addEventListener('click', () => { this.editingReport = null; this.editDraft = null; this.redrawFeed(); });
    c.querySelector<HTMLElement>('[data-eod-save]')?.addEventListener('click', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.eodSave!;
      const v = (sel: string) => (c.querySelector(`[${sel}]`) as HTMLTextAreaElement | null)?.value ?? '';
      const summary = v('data-ed-summary').trim();
      if (!summary) { alert('The summary cannot be empty.'); return; }
      try {
        const r = await fetch(`${API}/eod/${encodeURIComponent(id)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ summary, blockers: v('data-ed-blockers').trim(), tomorrow: v('data-ed-tomorrow').trim(), edited_by: this.actorName() || 'unknown', edited_by_email: this.actorEmail(), actor_role: this.actorRole() }),
        });
        const j = await r.json().catch(() => null);
        if (!r.ok) throw new Error((j && j.error) || `HTTP ${r.status}`);
        const ix = this.reports.findIndex(x => x.id === id);
        if (ix >= 0 && j?.report) this.reports[ix] = j.report;
        this.editingReport = null;
        this.editDraft = null;
        this.redrawFeed();
      } catch (err: any) { alert(`Save failed — ${err.message}`); }
    });
    c.querySelectorAll<HTMLInputElement>('[data-eod-photo]').forEach(inp => inp.addEventListener('change', async (e) => {
      const el = e.target as HTMLInputElement;
      const id = el.dataset.eodPhoto!;
      const files = Array.from(el.files || []);
      el.value = '';
      if (!files.length) return;
      try {
        await this.uploadEodPhotos(id, files);
        await this.loadEod();
        this.redrawFeed();   // R110 #3: the card stays expanded (its id is in eodOpen)
      } catch (err: any) { alert(`Photo upload failed — ${err.message}`); }
    }));
  }

  // R115 C5: AI card handlers — expand / collapse, Sources ⓘ, and the ⋯ items
  // (Post to Slack again · Copy text · Show facts). The ⋯ menu node moves into
  // a popover on open; handlers bound here travel with it.
  private bindAiCards(): void {
    const c = this.container;
    const byId = (id: string) => this.aiReports.find(x => x.id === id) || null;
    c.querySelectorAll<HTMLElement>('[data-eod-ai-expand]').forEach(el => {
      const open = () => { this.aiOpen.add(el.dataset.eodAiExpand!); this.redrawFeed(); };
      el.addEventListener('click', open);
      el.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    });
    c.querySelectorAll<HTMLElement>('[data-eod-ai-collapse]').forEach(el => el.addEventListener('click', e => {
      if ((e.target as HTMLElement).closest('button, a, summary, details, [data-kebab]')) return;
      this.aiOpen.delete(el.dataset.eodAiCollapse!);
      this.redrawFeed();
    }));
    c.querySelectorAll<HTMLElement>('[data-eod-ai-src]').forEach(b => b.addEventListener('click', () => {
      const id = b.dataset.eodAiSrc!;
      if (this.aiSrcOpen.has(id)) this.aiSrcOpen.delete(id); else this.aiSrcOpen.add(id);
      this.redrawFeed();
    }));
    const flash = (id: string, text: string) => {
      this.aiFlash = { id, text };
      this.redrawFeed();
      window.setTimeout(() => { if (this.aiFlash && this.aiFlash.id === id && this.aiFlash.text === text) { this.aiFlash = null; if (c.querySelector('#eod-feed')) this.redrawFeed(); } }, 3500);
    };
    c.querySelectorAll<HTMLElement>('[data-eod-ai-copy]').forEach(b => b.addEventListener('click', async () => {
      const r = byId(b.dataset.eodAiCopy!);
      if (!r) return;
      const text = this.aiPlainText(r);
      try { await navigator.clipboard.writeText(text); flash(r.id, 'Copied.'); }
      catch {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); flash(r.id, 'Copied.'); } catch { alert('Copy failed — select the text by hand.'); }
        ta.remove();
      }
    }));
    c.querySelectorAll<HTMLElement>('[data-eod-ai-facts]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.eodAiFacts!;
      if (this.aiFactsOpen.has(id)) { this.aiFactsOpen.delete(id); this.redrawFeed(); return; }
      this.aiFactsOpen.add(id); this.aiOpen.add(id);
      this.redrawFeed();
      if (this.aiFacts.has(id)) return;
      try {
        const r = await fetch(`${API}/eod/ai/${encodeURIComponent(id)}`);
        const j = await r.json().catch(() => null);
        if (!r.ok) throw new Error((j && j.error) || `HTTP ${r.status}`);
        this.aiFacts.set(id, (j && j.report && j.report.facts) || {});
      } catch (e: any) { this.aiFacts.set(id, { error: `Couldn't load the facts — ${e.message}` }); }
      if (c.querySelector('#eod-feed')) this.redrawFeed();
    }));
    c.querySelectorAll<HTMLElement>('[data-eod-ai-slack]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.eodAiSlack!;
      try {
        const r = await fetch(`${API}/eod/ai/${encodeURIComponent(id)}/post-slack`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
        const j = await r.json().catch(() => null);
        if (!r.ok) throw new Error((j && j.error) || `HTTP ${r.status}`);
        const rec = byId(id);
        if (rec && j && j.slack) rec.slack = j.slack;
        const sl = j && j.slack;
        flash(id, sl && sl.posted ? 'Posted to Slack.' : sl && sl.skipped === 'not_configured' ? 'Slack is not set up on this server — nothing was posted.'
          : sl && sl.skipped === 'off' ? 'The Slack post is switched off — turn on "Nightly AI end-of-day report" in Settings → Slack notifications.'
          : `Not posted${sl && sl.error ? ` — ${sl.error}` : ''}.`);
      } catch (e: any) { alert(`Post to Slack failed — ${e.message}`); }
    }));
  }

  // server caps multipart at 6 files/request — batch bigger picks
  private async uploadEodPhotos(id: string, files: File[]): Promise<void> {
    for (let i = 0; i < files.length; i += 6) {
      const fd = new FormData();
      fd.append('edited_by', this.actorName() || 'unknown');   // R48 per-author gate
      fd.append('edited_by_email', this.actorEmail());
      try { fd.append('actor_role', sessionStorage.getItem('userRole') || ''); } catch { /* gate falls back to author match */ }
      for (const f of files.slice(i, i + 6)) fd.append('photo', f, f.name);
      const r = await fetch(`${API}/eod/${encodeURIComponent(id)}/photo`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(`HTTP ${r.status}${i > 0 ? ` (after ${i} uploaded)` : ''}`);
    }
  }

  // R113 #6: HostBuddy chrome — every colour is a :root token (greens →
  // --success*, ambers → --warning*, the old #111827 dark buttons → .btn
  // variants from main.css), cards carry var(--radius) + var(--shadow-card).
  private injectStyles(): void {
    if (document.getElementById('eod-styles')) return;
    const s = document.createElement('style');
    s.id = 'eod-styles';
    s.textContent = `
      .eod-page { max-width: 860px; }
      .eod-seg { margin-bottom:16px; max-width:100%; }
      .eod-toolbar { display:flex; gap:10px; align-items:center; margin-bottom:12px; flex-wrap:wrap; }
      .eod-toolbar .input { width:auto; }
      .btn.eod-danger { color:var(--danger); border-color:var(--danger-border); }
      .btn.eod-danger:hover:not(:disabled) { background:var(--danger-soft); border-color:var(--danger); }
      .eod-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); margin-bottom:12px; overflow:hidden; box-shadow:var(--shadow-card); }
      .eod-card-head { display:flex; justify-content:space-between; align-items:center; gap:10px; padding:12px 16px; border-bottom:1px solid var(--border-soft); }
      .eod-card-row { display:flex; justify-content:space-between; align-items:center; gap:10px; padding:12px 16px; }
      .eod-meta { font-size:11.5px; color:var(--text-faint); }
      .eod-progress { font-size:12px; font-weight:700; color:var(--text-muted); background:var(--bg-subtle); border-radius:var(--radius-pill); padding:3px 10px; font-variant-numeric:tabular-nums; }
      .eod-progress.done { color:var(--success-text); background:var(--success-soft); }
      .eod-bar { height:3px; background:var(--bg-subtle); }
      .eod-bar-fill { height:100%; background:var(--success); transition:width .2s ease; }
      .eod-item { display:flex; align-items:flex-start; gap:10px; padding:9px 16px; border-bottom:1px solid var(--border-soft); font-size:13px; }
      .eod-item:last-child { border-bottom:none; }
      .eod-item:hover { background:var(--bg-subtle); }
      .eod-item.checked .eod-item-label { color:var(--text-faint); text-decoration:line-through; }
      .eod-item.na .eod-item-label { color:var(--text-faint); font-style:italic; }
      .eod-item input[type="checkbox"] { width:18px; height:18px; accent-color:var(--success); flex:none; cursor:pointer; margin-top:1px; }
      .eod-item-main { flex:1; min-width:0; }
      .eod-item-label { display:inline; }
      .eod-item-by { font-size:10.5px; color:var(--success-text); white-space:nowrap; margin-left:6px; }
      .eod-item-note { font-size:11.5px; color:var(--text-muted); margin-top:3px; }
      .eod-item-phs { display:flex; flex-wrap:wrap; gap:6px; margin-top:5px; }
      .eod-item-ph { width:52px; height:52px; object-fit:cover; border-radius:var(--radius-sm); border:1px solid var(--border); }
      .eod-item-acts { display:flex; gap:5px; flex:none; align-items:center; }
      .eod-na { border:1px solid var(--border); background:var(--card); border-radius:var(--radius-sm); font-size:10.5px; font-weight:600; color:var(--text-muted); padding:4px 7px; cursor:pointer; min-height:28px; font-family:inherit; }
      .eod-na:hover { border-color:var(--border-strong); color:var(--text); }
      .eod-na.on { background:var(--border-soft); color:var(--text); border-color:var(--border-strong); }
      .eod-form { padding:14px 16px; display:flex; flex-direction:column; gap:9px; }
      .eod-form input[type="text"], .eod-form textarea, .eod-form select { border:1px solid var(--border); border-radius:var(--radius-btn); padding:9px 12px; font-size:13px; font-family:inherit; background:var(--card); color:var(--text); resize:vertical; width:100%; box-sizing:border-box; transition:border-color .12s, box-shadow .12s; }
      .eod-form input[type="text"]:focus, .eod-form textarea:focus, .eod-form select:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
      .eod-form-row { display:flex; gap:8px; flex-wrap:wrap; }
      .eod-form-row > * { flex:1; min-width:160px; }
      .eod-form-row .btn { flex:none; }
      /* R113 #6: composer — collapsed by default, extra fields on focus / text */
      .eod-composer-head { display:flex; justify-content:space-between; align-items:baseline; gap:10px; padding:12px 16px 0; flex-wrap:wrap; }
      .eod-composer-head strong { font-size:13.5px; }
      .eod-composer .eod-form { padding-top:10px; }
      .eod-more { display:flex; flex-direction:column; gap:9px; }
      .eod-composer:not(.open) .eod-more { display:none; }
      .eod-form-acts { display:flex; gap:8px; align-items:center; justify-content:flex-end; flex-wrap:wrap; }
      .eod-filed { display:flex; align-items:center; gap:8px; flex-wrap:wrap; padding:11px 16px; font-size:13px; font-weight:600; color:var(--success-text); }
      /* R113 #6: ONE toolbar row — search outside #eod-feed-ctl, the rest inside it */
      .eod-feedbar { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin:4px 0 12px; }   /* NOT .eod-bar — that's the checklist card's 3px progress bar */
      .eod-search { position:relative; flex:1 1 220px; min-width:180px; max-width:300px; display:flex; align-items:center; color:var(--text-faint); }
      .eod-search svg { position:absolute; left:11px; pointer-events:none; }
      .eod-search .input { width:100%; min-width:0; padding:8px 10px 8px 32px; }
      #eod-feed-ctl { display:flex; align-items:center; gap:8px; flex-wrap:wrap; flex:1 1 auto; min-width:0; }
      .eod-sel { width:auto; min-width:150px; padding:7px 10px; }
      .eod-win { padding:2px; border-radius:var(--radius-btn); flex:none; }
      .eod-win .seg-tab { padding:5px 10px; font-size:11.5px; border-radius:var(--radius-sm); }
      .eod-win[hidden] { display:none; }
      .eod-count { margin-left:auto; font-size:12px; color:var(--text-faint); white-space:nowrap; font-variant-numeric:tabular-nums; }
      /* feed: day groups, one-line cards, search marks */
      .eod-day-head { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin:16px 0 8px; }
      .eod-day > summary { list-style:none; cursor:pointer; display:flex; gap:6px; align-items:baseline; flex-wrap:wrap; user-select:none; }
      .eod-day > summary::-webkit-details-marker { display:none; }
      .eod-day-head .eod-meta { text-transform:none; letter-spacing:0; font-weight:500; }
      .eod-min-caret { display:inline-block; font-size:9px; color:var(--text-faint); flex:none; margin-right:3px; transition:transform .12s ease; }
      .eod-day[open] > summary .eod-min-caret, .eod-min-caret.open { transform:rotate(90deg); }
      .eod-report { overflow:visible; }   /* the ⋯ menu must escape the card */
      .eod-report-min { display:flex; gap:6px; align-items:baseline; padding:9px 14px; font-size:12.5px; cursor:pointer; min-width:0; margin-bottom:6px; }
      .eod-report-min:hover { background:var(--bg-subtle); border-color:var(--border-strong); }
      .eod-report-min:focus-visible { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
      .eod-report-min .pill { flex:none; align-self:center; }
      .eod-min-sum { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:var(--text-muted); }
      .eod-report > .eod-card-head { border-radius:var(--radius) var(--radius) 0 0; flex-wrap:nowrap; }
      .eod-head-main { display:flex; align-items:baseline; gap:8px; min-width:0; flex:1; flex-wrap:wrap; }
      .eod-head-main strong { font-size:13px; }
      .eod-head-click { cursor:pointer; }
      .eod-head-click:hover { background:var(--bg-subtle); }
      .eod-hl { background:var(--warning-soft); color:var(--warning-text); border-radius:3px; padding:0 2px; box-shadow:inset 0 -1.5px 0 var(--warning); }
      .eod-report-body { padding:12px 16px; font-size:13px; line-height:1.55; white-space:pre-wrap; }
      .eod-report-sec { padding:0 16px 12px; font-size:12.5px; line-height:1.5; white-space:pre-wrap; }
      .eod-report-sec .pill { vertical-align:middle; margin-right:4px; }
      .eod-photos { display:flex; flex-wrap:wrap; gap:8px; padding:4px 16px 12px; }
      .eod-thumb { position:relative; width:96px; height:96px; padding:0; border:1px solid var(--border); border-radius:var(--radius-sm); overflow:hidden; background:var(--bg-subtle); cursor:zoom-in; display:block; }
      .eod-thumb:hover { border-color:var(--accent); }
      .eod-thumb:focus-visible { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
      .eod-thumb img, .eod-thumb video { width:100%; height:100%; object-fit:cover; display:block; }
      .eod-play { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-size:22px; color:var(--card); text-shadow:0 1px 4px rgba(15,23,42,.6); pointer-events:none; }
      .eod-edit-line { font-size:11px; color:var(--text-faint); padding:2px 16px 6px; }
      .eod-empty { padding:36px 24px; text-align:center; color:var(--text-faint); font-size:13px; border:1px dashed var(--border); border-radius:var(--radius); }
      .eod-empty-acts { display:flex; gap:8px; flex-wrap:wrap; justify-content:center; margin-top:10px; }
      .eod-empty-acts:empty { display:none; }
      /* R113 #6: the ⋯ menu — a native <details> (MaintenancePage .mn-dd pattern) */
      .eod-dd { position:relative; display:inline-block; flex:none; }
      .eod-dd > summary { list-style:none; }
      .eod-dd > summary::-webkit-details-marker { display:none; }
      .eod-ibtn { width:28px; height:28px; border-radius:var(--radius-sm); border:1px solid var(--border); background:var(--card); color:var(--text-muted); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; font-size:16px; font-weight:700; line-height:1; font-family:inherit; transition:border-color .12s, color .12s, background .12s; }
      .eod-ibtn:hover, .eod-dd[open] > .eod-ibtn { border-color:var(--accent); color:var(--accent); background:var(--accent-soft); }
      .eod-dd-menu { position:absolute; top:calc(100% + 4px); right:0; z-index:30; min-width:180px; background:var(--card); border:1px solid var(--border); border-radius:var(--radius-sm); padding:5px; display:flex; flex-direction:column; gap:2px; box-shadow:var(--shadow-pop); }
      .eod-dd-item { display:block; width:100%; text-align:left; padding:8px 10px; border:none; border-radius:calc(var(--radius-sm) - 2px); background:transparent; font:inherit; font-size:12.5px; font-weight:500; color:var(--text); cursor:pointer; white-space:nowrap; box-sizing:border-box; }
      .eod-dd-item:hover { background:var(--bg-subtle); color:var(--accent-hover); }
      /* R115 C5: the AI end-of-day report — the row above the composer + one card per day */
      .eod-ai-bar { padding:10px 14px; }
      .eod-ai-bar-row { display:flex; align-items:center; gap:8px 12px; flex-wrap:wrap; }
      .eod-ai-bar-main { display:flex; align-items:baseline; gap:4px 8px; flex-wrap:wrap; min-width:0; flex:1 1 220px; font-size:13px; }
      .eod-ai-bar-acts { display:flex; gap:8px; align-items:center; flex:none; margin-left:auto; }
      .eod-ai-msg { margin-top:8px; font-size:12px; color:var(--text-muted); }
      .eod-ai-noteform { padding:10px 0 0; }
      .eod-ai-notes { margin-top:8px; border-top:1px solid var(--border-soft); padding-top:6px; display:flex; flex-direction:column; gap:2px; }
      .eod-ai-note { display:flex; align-items:flex-start; gap:8px; font-size:12.5px; line-height:1.45; padding:3px 0; }
      .eod-ai-note-txt { flex:1; min-width:0; overflow-wrap:anywhere; color:var(--text); }
      .eod-ai-note-txt b { font-weight:600; }
      .eod-ai-notes-sub { margin-top:6px; font-size:var(--fs-label); font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
      .eod-ai-note.used .eod-ai-note-txt { color:var(--text-muted); }
      .eod-ai-x { flex:none; width:28px; height:28px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); color:var(--text-muted); cursor:pointer; font-size:15px; line-height:1; font-family:inherit; }
      .eod-ai-x:hover { border-color:var(--danger-border); color:var(--danger); background:var(--danger-soft); }
      .eod-ai-ic { flex:none; font-size:13px; }
      .eod-ai-min .pill, .eod-ai .eod-head-main .pill { flex:none; align-self:center; }
      .eod-ai-stub { display:flex; gap:6px; align-items:baseline; flex-wrap:wrap; padding:9px 14px; font-size:12.5px; margin-bottom:6px; border-style:dashed; box-shadow:none; color:var(--text-muted); }
      .eod-ai-body { padding:12px 16px 4px; font-size:13px; line-height:1.5; }
      .eod-ai-headline { font-size:14px; font-weight:600; color:var(--text); margin-bottom:8px; overflow-wrap:anywhere; }
      .eod-ai-warn { background:var(--warning-soft); color:var(--warning-text); border:1px solid var(--warning-border); border-radius:var(--radius-sm); padding:7px 10px; font-size:12px; margin-bottom:10px; }
      .eod-ai-sec { margin-bottom:10px; }
      .eod-ai-sec-t { font-size:var(--fs-label); font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin-bottom:3px; }
      .eod-ai-sec-t .pill { text-transform:none; letter-spacing:0; }
      .eod-ai-sec ul { margin:0; padding-left:18px; }
      .eod-ai-sec li { margin:2px 0; overflow-wrap:anywhere; }
      .eod-ai-foot { padding:8px 16px 12px; border-top:1px solid var(--border-soft); display:flex; flex-direction:column; gap:4px; }
      .eod-ai-srcline { display:flex; align-items:baseline; gap:6px; flex-wrap:wrap; }
      .eod-ai-srcbtn { border:none; background:none; padding:2px 0; font:inherit; font-size:12px; font-weight:600; color:var(--accent); cursor:pointer; white-space:nowrap; }
      .eod-ai-srcbtn:hover { color:var(--accent-hover); text-decoration:underline; }
      .eod-ai-srcbox { background:var(--bg-subtle); border:1px solid var(--border-soft); border-radius:var(--radius-sm); padding:8px 10px; font-size:12px; line-height:1.5; }
      .eod-ai-srcbox ul { list-style:none; margin:0 0 6px; padding:0; }
      .eod-ai-srcbox li { position:relative; padding:1px 0 1px 14px; }
      .eod-ai-dot { position:absolute; left:0; top:7px; width:7px; height:7px; border-radius:50%; background:var(--border-strong); }
      .eod-ai-dot.on { background:var(--success); }
      .eod-ai-cost { font-variant-numeric:tabular-nums; }
      .eod-ai-flash { font-size:12px; font-weight:600; color:var(--success-text); }
      .eod-ai-facts { margin:0 16px 12px; font-size:12px; }
      .eod-ai-facts > summary { cursor:pointer; color:var(--text-muted); font-weight:600; padding:4px 0; }
      .eod-ai-facts pre { margin:4px 0 0; max-height:320px; overflow:auto; background:var(--bg-subtle); border:1px solid var(--border-soft); border-radius:var(--radius-sm); padding:8px 10px; font-family:var(--font-mono); font-size:11px; line-height:1.45; white-space:pre-wrap; overflow-wrap:anywhere; }
      @media (max-width: 640px) {
        .eod-ai-bar-acts { flex:1 1 100%; margin-left:0; }
        .eod-ai-bar-acts .btn { flex:1; min-height:38px; justify-content:center; }
        .eod-ai-noteform .eod-form-acts .btn { min-height:38px; }
        .eod-ai-x { width:36px; height:36px; }
        .eod-ai .kb-btn { min-width:38px; height:36px; }
        .eod-ai-srcbtn { min-height:36px; }
        .eod-ai-body { padding:12px 14px 4px; }
        .eod-ai-foot { padding:8px 14px 12px; }
        .eod-ai-facts { margin:0 14px 12px; }
      }
      @media (max-width: 640px) {
        .eod-seg { display:flex; width:100%; }
        .eod-seg .seg-tab { flex:1; justify-content:center; padding:7px 6px; }
        .eod-search { flex-basis:100%; max-width:none; }
        .eod-count { margin-left:0; }
        .eod-report-min { flex-wrap:wrap; }
        .eod-min-sum { white-space:normal; flex-basis:100%; }
        .eod-card-head { flex-wrap:wrap; }
        .eod-report > .eod-card-head { flex-wrap:nowrap; }
      }
    `;
    document.head.appendChild(s);
  }
}
